// Singleton de player pra canais AO VIVO.
//
// Arquitetura:
// - 1 <video> + 1 MediaPlayer instance pra toda a sessão de TV ao vivo.
// - <video> mora num portal layer fixed off-screen quando idle. Quando
//   componente "ataca" (Hero/PlayerScreen), o <video> é REPATRIADO
//   (Node.appendChild) pra dentro do container target — sem destroy.
// - Re-load de canal não destrói player; só chama player.load() de novo.
//
// Fora desse contexto: VOD/series/replay/catchup criam players próprios.
// Não tocamos neles.
//
// Performance: cleanup automático após 30s sem attach (garbage collect).
// User assistindo por horas: troca canal = re-load (rápido), troca tela =
// destroy (mantém app leve).


import { getCapability } from '@app-iptv/device'
import { nextSource, type GroupedChannel } from '@app-iptv/iptv-client'
import { createPlayer, type MediaPlayer } from '@app-iptv/player'
import { getCurrentFocusKey } from '@app-iptv/ui'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { getStack, subscribe as subscribeOverlayStack } from '../detail-overlay-store'
import { clearManualSource, getManualSource, saveManualSource } from '../local-db'
import { notifyPlaybackStarted } from '../net-priority'
import { isCanvasScaled } from '../packaged-tv-scale'
import { track } from '../qoe'
import { signStreamDeduped } from '../stream-sign'
import { rewriteStreamUrl } from '../stream-url'

import { isBlockedStatus } from './blocked-status'
import {
  decideEndpointFailoverResolved,
  decideEndpointFailoverStart,
} from './endpoint-failover'
import { decideSignFailure } from './provider-outage'
import { statusOnMediaInfo, statusOnPlaying } from './live-audio-status'
import { getAppBasename, isLiveRoute } from './live-route'
import { LIVE_MARK, liveMark, recordLiveTtff } from './live-ttff'
import { createLoadGate, type LoadGate } from './load-gate'
import {
  initialLoadFailoverMs,
  openNoDataFailoverMs,
  shouldFailoverOnOpen,
} from './open-failover'
import { decidePlayAction } from './play-dedup'
import { createDestroyBarrier } from './player-barrier'
import {
  burstShouldContinue,
  crossedNavZone,
  hiddenStageStyle,
  objectFitForMode,
  shownStageStyle,
  STAGE_SENTINEL_MS,
  type SlotRect,
} from './stage-layout'
import type { AttachMode, LivePlayerError, LivePlayerState } from './types'

/** Rotas que PODEM consumir o singleton de live (Hero da LiveScreen
 *  ou PlayerScreen fullscreen). Qualquer outra rota → route guard
 *  chama doSuspend (pausa + agenda GC).
 *
 *  Por que isso existe: o GC de 30s (HIDDEN) ou 10s (SUSPEND) mantém o
 *  video tocando/em hidden layer enquanto user "só foi rapidinho lá em
 *  outra tela e volta". Mas se navegar pra EPG, Movies, Settings —
 *  premium = silêncio total imediato (suspend pausa o áudio).
 *
 *  Rotas que NÃO estão aqui (vod-player, series-player, catchup-player,
 *  /movies, /series, /epg, /settings, etc) usam players próprios e
 *  precisam que o singleton esteja suspendido pra evitar concorrência
 *  de áudio/banda. Fix #3 do refator 2026-05-16: comentário anterior
 *  dizia que catchup estava incluso aqui, mas a regex nunca cobriu
 *  /catchup/. Confirmado correto: CatchupPlayerScreen cria player
 *  próprio (não usa o singleton); incluir catchup aqui faria os 2
 *  coexistirem.
 *
 *  ⚠️ vod-player e series-player NÃO são mais rotas de verdade: viraram camadas
 *  da pilha de overlay (`/vod-player/:id` e `/series-player/...` são só shims de
 *  deep-link que empilham e redirecionam). A pilha não muda o path, então este
 *  guard de ROTA não os enxerga — quem solta o live por baixo deles é o "Guard
 *  de OVERLAY" dentro do provider. */
/**
 * "Estou numa rota de live?" — SEMPRE via `isLiveRoute`, nunca testando o
 * `location.pathname` cru contra os padrões. No empacotado (file://) o pathname
 * carrega o caminho de instalação inteiro e nenhum padrão `/^\/live/` casa; o
 * basename do router precisa sair antes. Ver `live-route.ts` (bug de áudio da
 * TV, achado na LG real). Padrões e helpers moram lá pra serem testáveis.
 */
const inLiveRoute = (): boolean => {
  try {
    if (typeof window === 'undefined' || !window.location) return false
    // getAppBasename() é IDEMPOTENTE (não muda depois de navegar). Usar aqui o
    // deriveRouterBasenameFromWindow() do router seria uma armadilha: no file://
    // ele devolve o pathname atual inteiro, já com a rota grudada. Ver live-route.ts.
    return isLiveRoute(window.location.pathname, getAppBasename())
  } catch {
    // SSR / location indisponível — trata como "fora da live" (comportamento antigo).
    return false
  }
}

/**
 * A pilha de overlay COBRE o live? Qualquer camada cobre: ficha e player são
 * `fixed` e opacos por cima da tela de origem, então com uma camada que seja o
 * consumidor do live não é visto — e o player, além disso, disputa o
 * decodificador. Lido da PILHA, síncrono, no instante da decisão — nunca de
 * estado do React. Ver o bloco "Guard de OVERLAY" no provider: a pilha não muda a
 * rota, e o route guard acima não a enxerga.
 *
 * Era "há PLAYER na pilha", e o smoke da LG (2.39.0) mostrou o furo: saindo do
 * episódio com a ficha ainda na tela, o canal voltava a tocar ATRÁS dela (dava
 * pra ouvir, não pra ver). O live só toca com a superfície livre.
 */
const overlayCoversLive = (): boolean => getStack().length > 0

/** Um pedido de carga do live: o que o `doLoad` recebe e o que a volta recarrega. */
interface LiveRequest {
  channelId: string
  group: GroupedChannel
}

// ===== Context =====

export type AutoSwitchReason =
  | 'sign'
  | 'blocked'
  | 'initial-error'
  | 'runtime-error'
  | 'stalled'

/**
 * Actions context (estável). Refs internos, NUNCA muda entre renders.
 * Componentes que só precisam chamar funções (não ler state) consomem
 * via useLivePlayerActions e não re-renderizam quando state muda.
 *
 * CRÍTICO: separação evita loop. Antes, quando attachTo() chamava
 * setState({attachMode}), o context value mudava → Hero/PlayerScreen
 * re-rendiziam → useEffect [livePlayer] disparava → attachTo de novo
 * → loop infinito.
 */
interface LivePlayerActions {
  play: (channelId: string, group: GroupedChannel) => void
  selectSource: (channelId: string) => void
  /**
   * Retry "fresh" do canal atual: reseta `attemptedSourcesRef` e re-dispara
   * o doLoad. Usar no botão "Tentar de novo" do overlay de erro — o
   * `play()` normal não reseta attempted no mesmo grupo, então o retry
   * tentaria a mesma fonte que acabou de falhar. Aqui força um fresh start
   * pra testar fontes que talvez tenham se recuperado.
   */
  retry: () => void
  attachTo: (container: HTMLElement | null, mode: AttachMode) => void
  subscribe: (
    event: 'playing' | 'buffering' | 'error' | 'ready' | 'autoSwitch',
    handler: (payload?: LivePlayerError | AutoSwitchReason) => void,
  ) => () => void
  destroy: () => void
}

const LivePlayerStateContext = createContext<LivePlayerState | null>(null)
const LivePlayerActionsContext = createContext<LivePlayerActions | null>(null)

// ===== Provider =====

const HIDDEN_GC_DELAY_MS = 30_000 // destrói após 30s sem attach (hero unmount)
// Suspend GC: bem mais agressivo. User saiu da família /live —
// se não voltar em 10s, destrói TUDO (buffer Shaka + player + listeners).
// Trade-off: retorno em < 10s = retomada instantânea sem reload; > 10s
// = reload (custo aceitável). Justificativa de RAM: vlow Tizen 2017
// não pode ficar com 10-30s de buffer MSE pendurado em background.
const SUSPEND_GC_DELAY_MS = 10_000
// WALL 1 (perf/tv-zap-quickwins) — teto da barreira de recriação do player.
// A barreira normalmente resolve MUITO antes (no evento 'destroyed', que é o
// fim do teardown — dezenas de ms). Este teto é só a rede de segurança
// anti-deadlock: se 'destroyed' NUNCA disparar (bug degenerado), o zap segue
// em vez de travar pra sempre. Generoso a ponto de nunca cortar um destroy
// saudável, curto a ponto de não parecer travado no pior caso.
const DESTROY_BARRIER_TIMEOUT_MS = 2_000

export function LivePlayerProvider({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  // <video> element vive aqui — nunca é desmontado enquanto provider tá vivo.
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // MediaPlayer instance — recriada em destroy(); persiste através de play()s.
  const playerRef = useRef<MediaPlayer | null>(null)
  // WALL 1 — barreira de recriação. Enquanto um doDestroy roda seu teardown
  // no <video> SINGLETON, esta ref segura a promise que resolve no 'destroyed'
  // do player anterior (ou num timeout de segurança). Um doLoad concorrente
  // (reopen dwell~0) espera essa barreira ANTES de criar/attachar o player
  // novo — senão o runDestroy do anterior faz video.load() em cima do load
  // novo (MediaError 4 / sign preso >15s). null = sem teardown em curso →
  // zap normal não paga custo nenhum.
  const destroyBarrierRef = useRef<Promise<void> | null>(null)
  // Container atual onde o video foi movido (Hero ou PlayerScreen)
  const currentContainerRef = useRef<HTMLElement | null>(null)
  // Layer escondida / STAGE do video. No mpegts é a "casa" 1x1 escondida (o
  // video é appendChild-ado pra fora, comportamento de sempre). No ramo
  // native-ts (fixedVideoMode) vira o STAGE reposicionável: o video NUNCA sai
  // dele — é reposicionado por CSS pro slot do Hero/fullscreen.
  const hiddenLayerRef = useRef<HTMLDivElement | null>(null)
  // Handoff SEM re-parent (native-ts). No webOS punch-through o appendChild do
  // <video> ESVAZIA o elemento nativo (readyState 0 → reload ~2s a cada
  // Hero↔fullscreen — medido no device). Fix: manter o <video> FIXO no stage e
  // reposicionar por CSS. Gate: SÓ nativeTsPlayback E sem a tática B de escala
  // (isCanvasScaled) — sob transform no #root, position:fixed +
  // getBoundingClientRect divergem de espaço de coordenadas, então caímos no
  // appendChild (sem regressão; firmware que ignora viewport é raro e já
  // degradado). mpegts nunca entra aqui. Lido 1x: ambos os sinais são estáveis
  // na sessão (plataforma + attribute de boot).
  // ===== HISTÓRICO: o P0 do #530, e por que a flag voltou pra `true` =====
  // O #530 desligou este ramo depois que a LG real mostrou o <video> singleton
  // sem sustentar carga — networkState/readyState presos em 0, 0 frames — em
  // NENHUM canal, no Hero E no fullscreen. Com a FASE 1 do failover gated em
  // `readyState===0` (shouldFailoverOnOpen), ela rotaciona, ESGOTA as fontes e
  // escreve "Canal indisponível" no catálogo inteiro. O changelog registrou o
  // custo de voltar pro appendChild (~2s de tela preta por transição) como
  // "trade-off ACEITO" — o dono do produto nunca aceitou, e cobrou de novo.
  //
  // A RAIZ (fechada agora): não era o `<video>` fixo nem o punch-through. Era a
  // visibilidade do stage ser CONSEQUÊNCIA de quem mexe no player. `doSuspend` e
  // `doDestroy` escondiam o stage E zeravam `currentContainerRef` — que é o gate
  // de todo caminho de mostrar. Com o consumidor montado, qualquer um dos dois
  // (o GC, ou `actions.destroy()` do trial bloqueado) deixava o <video> em 1x1 /
  // opacity 0 / z-index -1 PERMANENTEMENTE, e no punch-through do webOS um
  // <video> assim não carrega. Ver o bloco do invariante em `stageShouldShow` /
  // `reconcileStage`: a visibilidade passou a ser função de um invariante
  // próprio, o ref tem um dono só (`attachTo`), e o estado deixou de ser
  // absorvente. Gate que prende isso sem device: `stage-visibility.dom.test.tsx`.
  //
  // ⚠️ O diagnóstico original tinha um confusor não eliminado: o "controle"
  // que absolveu stream/conta/rede foi um <video> AVULSO criado no device via
  // CDP. O webOS tem UM plano de decoder de HW — um <video> concorrente é
  // candidato a CAUSAR `readyState` 0 no <video> do app. O probe de device
  // (`scripts/perf/handoff-reparent-probe.mjs`) agora recusa medir se houver
  // mais de um <video> no documento.
  const FIXED_VIDEO_MODE_ENABLED = true
  const fixedVideoModeRef = useRef<boolean | null>(null)
  if (fixedVideoModeRef.current === null) {
    let fixed = false
    try {
      fixed =
        FIXED_VIDEO_MODE_ENABLED &&
        getCapability('nativeTsPlayback') === true &&
        !isCanvasScaled()
    } catch {
      fixed = false
    }
    fixedVideoModeRef.current = fixed
  }
  // Watchdog de garbage collection
  const gcTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Timestamp (Date.now()) do primeiro suspend de uma cadeia. Permite
   * janela absoluta de SUSPEND_GC_DELAY_MS desde o primeiro suspend —
   * evita que navegação rápida fora de /live (movies → settings → epg)
   * adie o GC indefinidamente. Resetado quando user volta (attachTo
   * com container) ou quando o player é destruído.
   */
  const firstSuspendAtRef = useRef<number | null>(null)
  // Fallback central: IDs já tentadas no grupo atual (reset ao trocar grupo).
  // Hero e PlayerScreen NÃO orquestram — só consomem state. Quando o player
  // emite erro, o Provider automaticamente tenta a próxima source do grupo.
  const attemptedSourcesRef = useRef<Set<string>>(new Set())
  // F3 — failover de ENDEREÇO do provider (espelho do attemptedSourcesRef).
  // Quando todas as fontes do canal esgotam num endpoint (ou o canal só tem 1
  // fonte), o app pede ao backend o PRÓXIMO endpoint do provider via re-sign com
  // `avoidEndpointId`. Este Set guarda os endpointIds já tentados nesta cadeia de
  // falha — anti-loop: quando o backend devolve um endpointId já tentado (esgotou
  // os endereços), paramos de vez (erro ao usuário), NUNCA loop infinito de re-sign.
  // Reset junto com attemptedSourcesRef (troca de grupo, 30s estável, standby longo).
  const attemptedEndpointsRef = useRef<Set<string>>(new Set())
  // F3 — endpointId que o ÚLTIMO sign resolveu (vem no SignedStreamUrl). É o que
  // pedimos pra evitar no próximo re-sign de failover de endereço. Atualizado em
  // doLoad a cada sign bem-sucedido; null se a resposta não trouxe (retrocompat).
  const lastEndpointIdRef = useRef<string | null>(null)
  // fix/tv-provider-unavailable-no-storm — back-off de PROVEDOR-FORA.
  // Quando o sign responde 503 provider_unavailable, rotacionar fonte NÃO adianta
  // (todas falhariam) e é o que floda o edge do provedor. Em vez disso, esperamos
  // retryAfter e re-tentamos a MESMA fonte, com teto. `retriesRef` conta as
  // re-tentativas do TAP atual (resetado em todo load NÃO-back-off); `timerRef`
  // é o setTimeout pendente (cancelado por um zap/destroy). Ver provider-outage.ts.
  const providerUnavailableRetriesRef = useRef(0)
  const providerBackoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // F3 — teto absoluto de rodadas de failover de endereço por cadeia de falha.
  // Defesa-em-profundidade: o attemptedEndpointsRef já barra re-tentar o mesmo
  // endpoint, mas este teto garante terminação mesmo num cenário patológico (ex:
  // backend devolvendo ids sempre novos). Esgotou = erro normal, sem loop.
  const MAX_ENDPOINT_FAILOVER_ATTEMPTS = 4
  const endpointFailoverCountRef = useRef<number>(0)
  // Marca de "playing already happened" — distingue erro inicial vs runtime.
  const hasPlayedRef = useRef(false)
  // Verdade do DEMUXER sobre o canal atual: `hasVideo` do mpegts MEDIA_INFO.
  // `null` = ainda desconhecido (default "tem vídeo": nunca escondemos vídeo que
  // pode existir). `false` = rádio confirmado (sem ES de vídeo no PMT) → único
  // caso que liga o overlay audio-only. `true` = canal de vídeo. Resetado a cada
  // doLoad. NUNCA derivamos isso de `video.videoWidth===0` (racy — o áudio
  // decoda antes do 1º keyframe e marcava vídeo real como "só áudio").
  const mediaHasVideoRef = useRef<boolean | null>(null)
  // Watchdog de stalled — buffering > 15s sem progredir = troca fonte.
  const stalledTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Token único pra cada `play()`. Usado pra abortar lookups assíncronos
  // (getManualSource) que resolvem depois do user já ter zappeado pra
  // outro canal. Sem isso (B1 do review do auto-switch), a promise antiga
  // chamava `doLoad` clobberando o canal novo. Comparação por identidade
  // (Symbol) — não há colisão possível.
  const pendingPlayTokenRef = useRef<symbol | null>(null)
  // Gate SÍNCRONO da carga ativa (perf/tv-live-zap-failover). Cada doLoad chama
  // `begin()` no corpo síncrono e checa `isActive(token)` nos checkpoints
  // assíncronos. DIFERENTE de `stateRef.current.channelId` (atualizado por
  // useEffect pós-commit, com LAG), o token invalida a carga anterior na MESMA
  // volta do event loop do zap — então a carga intermediária de um burst aborta
  // ANTES de player.load()/GET do stream, em vez de escorregar pelo guard
  // defasado e abrir N conexões que serializam no pool minúsculo da TV. Ver
  // load-gate.ts pro racional completo.
  const loadGateRef = useRef<LoadGate | null>(null)
  if (loadGateRef.current === null) loadGateRef.current = createLoadGate()
  // ===== Estado PRÓPRIO do guard de overlay (ver o bloco "Guard de OVERLAY") =====
  // Último pedido de carga que o `doLoad` ACEITOU. Escrito SÍNCRONO no corpo do
  // doLoad — não é o `stateRef`, que só assenta num efeito pós-commit e pode estar
  // defasado no instante em que a pilha sobe.
  const liveRequestRef = useRef<LiveRequest | null>(null)
  // O que recarregar quando a pilha ficar VAZIA: o pedido aceito que estava vivo
  // quando a pilha cobriu o live, ou o pedido mais novo que chegou DURANTE (e
  // ficou estacionado). Consumido (zerado) quando a pilha esvazia.
  const overlayResumeRef = useRef<LiveRequest | null>(null)
  // Último veredito aplicado da pilha ("cobre o live?"). Escrito SÓ pelo listener da
  // pilha, a partir de `getStack()`; serve pra agir na TRANSIÇÃO e não a cada
  // mutação (o auto-avanço troca o player do topo sem mudar o veredito).
  const overlayBlockedRef = useRef<boolean>(false)
  // Watchdog de carga inicial (P3 #9 do review): se o canal NUNCA chega
  // a 'playing'/'ready' em 10s (sem player emitir error fatal), dispara
  // auto-switch. Cobre cenário onde player fica preso em loading
  // indefinidamente — rede muito ruim, stream que aceita conexão TCP
  // mas não entrega bytes, etc. Diferente do stalled (que só arma
  // após primeiro play), este cobre o GAP entre `doLoad` e primeiro
  // 'playing'. Armado em `doLoad`, limpo em `playing`/`ready`/`error`.
  const initialLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Failover da ABERTURA em DUAS FASES (perf/tv-live-zap-failover, item 2). O
  // stall inicial (antes do 1º frame) era governado SÓ pelo backstop tier-aware
  // de 10-20s — uma fonte morta ficava ~10s em "Carregando". Agora:
  //   FASE 1 (`openFailoverTimerRef`, ~4,5s): "nada chegou" — <video> preso em
  //     HAVE_NOTHING. Falha rápido, gated por readyState (poupa fonte lenta-viva).
  //   FASE 2 (`initialLoadTimerRef`, 10-20s): backstop "veio metadata mas travou"
  //     — comportamento pré-existente, byte-idêntico. Thresholds/decisão puros e
  //     testados em open-failover.ts. Mid-stream (pós-1º frame) NÃO muda.
  const openFailoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // M3 do review: tier-aware. vlow Tizen 2017 + 3G real pode legitimamente
  // levar 8-14s pra primeira playback (HLS parse + key fetch + segment).
  // Watchdog 10s nesse tier dispararia switch falso. low também é
  // conservador. mid+ usa 10s normal. (FASE 2 — ver open-failover.ts.)
  const getInitialLoadTimeoutMs = (): number => {
    try {
      return initialLoadFailoverMs(getCapability('cpuTier'))
    } catch {
      return initialLoadFailoverMs('mid')
    }
  }
  // FASE 1 — teto do "nada chegou". Muito menor que a FASE 2; ver open-failover.ts.
  const getOpenFailoverMs = (): number => {
    try {
      return openNoDataFailoverMs(getCapability('cpuTier'))
    } catch {
      return openNoDataFailoverMs('mid')
    }
  }
  // Timer de estabilização — após 30s tocando, reset de attempted (permite
  // retentar fontes ruins se voltar a falhar).
  const stabilizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Manual source TTL: quando user escolhe uma fonte específica via
  // SourceSwitcher, respeitamos a escolha por 30min — auto-switch
  // suspenso. Após o TTL expirar, volta ao automático (user pode ter
  // saído ou esquecido). Timestamp da última escolha manual.
  const manualSourceAtRef = useRef<number | null>(null)
  const MANUAL_SOURCE_TTL_MS = 30 * 60 * 1000
  const isManualActive = (): boolean => {
    const at = manualSourceAtRef.current
    return at !== null && Date.now() - at < MANUAL_SOURCE_TTL_MS
  }
  // Freeze watchdog: alguns canais travam SEM o player emitir 'buffering'
  // ou 'error' — videoEl.currentTime simplesmente para de avançar (stream
  // server caiu, network packet loss silencioso). Polling a cada 2s; se
  // currentTime ficar parado > 8s, dispara stalled (rotaciona fontes).
  const freezeWatchRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastVideoTimeRef = useRef<number>(0)
  const lastAdvancedAtRef = useRef<number>(0)
  // Gesture gate: web em dev sem interação prévia bloqueia autoplay
  // (NotAllowedError). Em vez de prender state em 'loading', voltamos
  // a 'idle' e armamos listener once-per-failure que retoma play() no
  // primeiro pointerdown/keydown/touchstart. Em TV nativa nunca arma.
  const gestureWaiterRef = useRef<(() => void) | null>(null)
  // Subscribers externos por evento — fan-out pra múltiplos consumidores
  const subscribersRef = useRef<{
    playing: Set<() => void>
    buffering: Set<() => void>
    ready: Set<() => void>
    error: Set<(err: LivePlayerError) => void>
    /** Disparado quando Provider troca pra fonte de fallback. UI usa pra
     *  mostrar toast "tentando outra fonte". */
    autoSwitch: Set<(reason: AutoSwitchReason) => void>
  }>({
    playing: new Set(),
    buffering: new Set(),
    ready: new Set(),
    error: new Set(),
    autoSwitch: new Set(),
  })

  const [state, setState] = useState<LivePlayerState>({
    status: 'idle',
    channelId: null,
    group: null,
    attachMode: null,
    error: null,
    notice: null,
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  })

  // ===== Helpers =====

  const fanOut = useCallback(
    (
      event: 'playing' | 'buffering' | 'ready' | 'error' | 'autoSwitch',
      payload?: LivePlayerError | AutoSwitchReason,
    ) => {
      const subs = subscribersRef.current
      if (event === 'error') {
        subs.error.forEach((h) => h(payload as LivePlayerError))
        return
      }
      if (event === 'autoSwitch') {
        subs.autoSwitch.forEach((h) => h(payload as AutoSwitchReason))
        return
      }
      subs[event].forEach((h) => h())
    },
    [],
  )

  // Refs pra evitar stale closures dentro de event handlers do MediaPlayer
  // (handlers são registrados 1x por player, mas state evolui).
  const stateRef = useRef<LivePlayerState>(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // ===== Stage do <video> fixo (native-ts, sem re-parent) =====
  /**
   * O stage está com a geometria VISÍVEL aplicada (`shownStageStyle`)?
   *
   * ⚠️ PRÉ-FILTRO BARATO DO SENTINELA, **não** um guard. Quem decide de verdade
   * é `stageShouldShow()`, dentro do `syncStageToContainer` — e ele já barra
   * tudo antes de qualquer `getBoundingClientRect`. Travar este ref em `true`
   * não muda comportamento nenhum (o review mediu: mutante quase-equivalente,
   * suíte inteira verde), e o texto anterior aqui vendia o contrário.
   *
   * O que ele economiza é real mas modesto: com o stage escondido, o sentinela
   * acorda a cada 400ms e nem chega a chamar `syncStageToContainer`. Fica pelo
   * custo/legibilidade, não porque segure invariante.
   *
   * Escrito só pelos dois aplicadores (`showStageAt` / `hideStage`), que são os
   * únicos caminhos que tocam a geometria do stage.
   */
  const stageVisibleRef = useRef<boolean>(false)
  // Aplica um mapa de estilos imperativamente no stage (a hiddenLayerRef) e o
  // object-fit no <video>. Decisão de estilo é PURA (stage-layout.ts); aqui é só
  // o glue com o DOM. No-op se o stage ainda não montou.
  const applyStageStyle = useCallback(
    (style: Record<string, string>, fit?: 'cover' | 'contain') => {
      const stage = hiddenLayerRef.current
      if (stage) {
        for (const [k, val] of Object.entries(style)) {
          stage.style.setProperty(
            k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase()),
            val,
          )
        }
      }
      const v = videoRef.current
      if (v && fit) {
        v.style.display = 'block'
        v.style.objectFit = fit
      }
    },
    [],
  )

  // Posiciona o stage sobre `rect` e o deixa visível, com object-fit do modo.
  // Aplicador CRU (sem change-detection); use via syncStageToContainer.
  const showStageAt = useCallback(
    (rect: SlotRect, mode: AttachMode) => {
      applyStageStyle(shownStageStyle(rect), objectFitForMode(mode))
      stageVisibleRef.current = true
    },
    [applyStageStyle],
  )

  // Esconde o stage (1x1 fora da tela). NUNCA chamado direto por um caller de
  // lifecycle — só por `reconcileStage`, que é quem conhece o invariante. Ver
  // o bloco abaixo. SÓ fixedVideoMode.
  const hideStage = useCallback(() => {
    applyStageStyle(hiddenStageStyle())
    stageVisibleRef.current = false
  }, [applyStageStyle])

  // ===== O INVARIANTE de visibilidade do stage (raiz do P0 do #530) =====
  // No ramo appendChild a visibilidade do <video> é o DOM: ele está fisicamente
  // dentro do slot, e destruir o player ou suspender não o tira de lá. No ramo
  // fixo o <video> nunca sai do stage, então a visibilidade virou ESTADO
  // IMPERATIVO — e era estado escrito por DOIS caminhos de esconder
  // (`doSuspend`, `doDestroy`) contra UM de mostrar (`attachTo` no mount do
  // consumidor). Ambos os caminhos de esconder também ZERAVAM
  // `currentContainerRef`, que é o gate de todo caminho de mostrar. Resultado: um
  // `doSuspend`/`doDestroy` com o Hero MONTADO deixava o <video> em 1x1,
  // opacity 0, z-index -1 PERMANENTEMENTE — só o remount do Hero saía de lá. No
  // punch-through do webOS um <video> nesse estado não sustenta carga:
  // `readyState` fica em 0, `shouldFailoverOnOpen` (gated em `readyState===0`)
  // rotaciona e ESGOTA as fontes, e o catálogo inteiro vira "Canal indisponível".
  // É o P0 que o #530 mitigou desligando a feature. `actions.destroy()` do
  // trial bloqueado (PlayerScreen) é um caminho REAL pra esse estado.
  //
  // Fix: a visibilidade deixa de ser CONSEQUÊNCIA de quem mexe no player e passa
  // a ser função de um invariante PRÓPRIO, com uma única definição —
  //     "há consumidor pedindo o vídeo E estamos na família live".
  // Ninguém mais escreve `currentContainerRef`: só o `attachTo` (o ciclo de vida
  // do consumidor). O destroy do player não muda ONDE o vídeo mora — igualzinho
  // ao ramo appendChild, que é o comportamento provado em produção.
  //
  // O invariante também substitui, com vantagem, o hack que `doSuspend` fazia de
  // zerar o ref "pra o burst não re-exibir o stage": agora quem barra é o próprio
  // `syncStageToContainer`, então nenhum keydown/resize/rAF re-exibe fora da
  // live — e o estado deixa de ser ABSORVENTE: quando a rota volta a dizer a
  // verdade, o `checkRoute` reconcilia e o vídeo reaparece sem remount.
  //
  // O invariante tem QUATRO termos, e cada um veio de um jeito de errar:
  //
  //  1. `fixedVideoModeRef` — fora do ramo fixo nada disto existe.
  //  2. `currentContainerRef` — há um consumidor pedindo o vídeo.
  //  3. `c.isConnected` — e esse consumidor ainda está NO DOCUMENTO. Sem este
  //     termo, o stage segue a geometria de um nó já removido: o
  //     `getBoundingClientRect` de um nó desconectado é 0x0, e o stage degenera
  //     pra `0,0,0x0` com `opacity: 1` — ou seja, VISÍVEL COM TAMANHO ZERO, que
  //     não é o `hiddenStageStyle` e é exatamente o predicado que o nosso
  //     próprio probe de device conta como frame inválido
  //     (`width <= 1 || height <= 1`).
  //  4. `mediaArmedRef` — e há MÍDIA armada neste <video>. Sem este termo, um
  //     caminho que restaura geometria sem restaurar playback entrega, no
  //     punch-through, um RETÂNGULO VISÍVEL SEM FRAMES — o mesmo quadro que
  //     alimenta "canal indisponível". Era o furo do primeiro corte deste fix:
  //     o `doSuspend` aborta o fetch e mata o transmuxer (e `resume()` não
  //     existe, foi removido de propósito), então reentrar na live sem remount
  //     do consumidor mostrava um vídeo mudo e parado. Geometria e playback
  //     andam juntos: ou os dois voltam, ou nenhum.
  const stageShouldShow = useCallback((): boolean => {
    if (!fixedVideoModeRef.current) return false
    const c = currentContainerRef.current
    if (c === null) return false
    if (!c.isConnected) return false
    if (!mediaArmedRef.current) return false
    return inLiveRoute()
  }, [])

  // ===== Tracking do slot (o slot do Hero SE MOVE) =====
  // Medido no device: o slot do Hero desce/sobe ~106px quando a TopNav
  // expande/colapsa. Uma medição única no attach (mesmo com 1 rAF) NÃO acompanha
  // isso → o vídeo descolava do card. Solução: re-medir o rect do container e
  // reaplicar SÓ quando muda (change-detection por chave), num BURST de rAF que
  // acompanha o movimento e se autolimita (política pura em `stage-layout.ts`:
  // silêncio geométrico de 200ms, teto de 2000ms — NÃO é mais prazo fixo de
  // 500ms, que era o defeito de 03/09), MAIS um SENTINELA de 400ms que é a rede
  // de convergência quando o layout assenta depois do burst.
  //
  // ⚠️ IDLE NÃO É CUSTO ZERO, e uma versão anterior deste bloco afirmava que era
  // ("IDLE = custo ZERO, sem rAF perpétuo"). Com o preview VISÍVEL o sentinela
  // custa 2,50 `getBoundingClientRect`/s (medido); com o stage escondido, 0/s. O
  // que continua verdade é o que o número protege: não há rAF perpétuo, e o
  // surfe horizontal não ARMA o burst — ou seja, não paga medição POR FRAME.
  // (Ele NÃO fica sem medir nada: o sentinela mede no surfe exatamente como em
  // repouso, 2,50/s. Medido — 10 leituras em 4s de surfe. O ganho é o burst que
  // não abre, não uma janela sem custo.) fullscreen: o videoBox é estático
  // (0,0,1920,1080) → o burst reaplica a mesma chave 1x e para no silêncio.
  //
  // O GATILHO do burst é o INVARIANTE, não o proxy: o slot só se move quando a
  // TopNav colapsa/expande, e isso é a ZONA de foco (`nav-*`), não o tipo de
  // tecla. Ver crossedNavZone (stage-layout.ts) e o poll de foco abaixo — pega
  // seta vertical, BACK hierárquico (setFocus('nav-live')) e setFocus('nav-*')
  // programático, e NÃO arma no surfe horizontal. Mais attach (mount) e resize.
  const attachModeRef = useRef<AttachMode>(null)
  /**
   * Há mídia ARMADA (carregando ou tocando) neste `<video>`?
   *
   * Não é "o player existe": no começo do `doLoad` ainda não existe player, e é
   * justamente aí que o `<video>` precisa já estar visível no slot — no
   * punch-through do webOS, carregar num elemento invisível é o que produz
   * `readyState` preso em 0. E não é "está tocando": entre o `load()` e o 1º
   * frame o stage tem que estar no lugar.
   *
   * `true` quando um `doLoad` começa; `false` no `doSuspend` (que aborta o fetch
   * e mata o transmuxer) e no `doDestroy`. É o termo do invariante que impede o
   * stage de mostrar um retângulo sem frames.
   */
  const mediaArmedRef = useRef<boolean>(false)
  const stageRafRef = useRef<number | null>(null)
  const lastStageKeyRef = useRef<string>('')
  // Relógio e memória do burst corrente (ver burstShouldContinue, stage-layout).
  const burstStartAtRef = useRef<number>(0)
  const burstLastChangeAtRef = useRef<number>(0)
  const burstSawMovementRef = useRef<boolean>(false)
  const burstExpectMovementRef = useRef<boolean>(false)
  const stageSentinelRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Último focusKey visto pelo poll de zona (pra detectar o cruzamento nav↔conteúdo).
  const lastNavFocusKeyRef = useRef<string>('')

  /** Resultado de uma sincronização: houve container utilizável, e a geometria
   *  MUDOU nesta passada? O `changed` é o sinal de movimento que o burst usa pra
   *  decidir parar — antes ele só tinha o relógio, que é proxy. */
  interface StageSyncResult {
    hasContainer: boolean
    changed: boolean
  }

  // Lê o rect do container attached, e — só se mudou — reaplica o estilo do
  // stage. `hasContainer` false quando não há container ou o invariante barra
  // (o burst usa pra parar cedo).
  const syncStageToContainer = useCallback((): StageSyncResult => {
    const c = currentContainerRef.current
    if (!c) return { hasContainer: false, changed: false }
    // Guard do INVARIANTE, não do proxy: o burst/rAF/resize NUNCA re-exibe o
    // stage fora da família live, sem precisar que alguém zere o container.
    if (!stageShouldShow()) return { hasContainer: false, changed: false }
    const r = c.getBoundingClientRect()
    const mode = attachModeRef.current
    const key = `${Math.round(r.left)},${Math.round(r.top)},${Math.round(
      r.width,
    )},${Math.round(r.height)},${String(mode)}`
    if (key !== lastStageKeyRef.current) {
      lastStageKeyRef.current = key
      showStageAt(r, mode)
      return { hasContainer: true, changed: true }
    }
    return { hasContainer: true, changed: false }
  }, [showStageAt, stageShouldShow])

  const stopStageTracking = useCallback(() => {
    if (stageRafRef.current != null) {
      cancelAnimationFrame(stageRafRef.current)
      stageRafRef.current = null
    }
    if (stageSentinelRef.current != null) {
      clearTimeout(stageSentinelRef.current)
      stageSentinelRef.current = null
    }
    burstExpectMovementRef.current = false
    burstSawMovementRef.current = false
    lastStageKeyRef.current = ''
  }, [])

  /**
   * ===== O SENTINELA (a rede que o burst não é) =====
   *
   * O burst SEMPRE termina — por silêncio ou pelo teto — e o caminho de término
   * NÃO esconde o stage: `syncStageToContainer` devolvendo `hasContainer:false`
   * só PARA o rastreio, e a geometria velha fica lá, escrita e visível. Logo,
   * qualquer janela em que o layout se mexa com o burst já encerrado congela o
   * preview num retângulo stale — foi o que o CDP mediu na LG (stage na posição
   * da nav EXPANDIDA sobre uma tela já COLAPSADA, cobrindo a faixa de chips).
   *
   * São DUAS janelas, e nenhuma delas é hipotética:
   *  1. o layout encosta DEPOIS do teto do burst (o render da TopNav com
   *     `data-collapsed` novo pode custar até ~1,7 s no tier low — número medido
   *     nesta casa, ver TopNav.tsx);
   *  2. o invariante fica transitoriamente falso durante o burst (mídia
   *     desarmada, consumidor fora do documento) e o burst morre no 1º frame.
   *
   * Por isso a convergência NÃO pode depender do burst. O sentinela é de baixa
   * frequência e roda SÓ quando não há burst e o stage está visível — ou seja,
   * exatamente na janela em que ninguém mais está olhando. Custo ocioso: ~2,5
   * `getBoundingClientRect`/s, e ZERO enquanto o burst roda ou o stage está
   * escondido. Não é `setInterval`: um timer recursivo não empilha se um tick
   * atrasar no aparelho lento.
   *
   * ⚠️ Ele chama APENAS `syncStageToContainer` — reposiciona, nunca decide
   * visibilidade. `reconcileStage` mexe em playback (`muted`, `play()`) e não
   * pode rodar a cada 400ms.
   */
  const armStageSentinel = useCallback(() => {
    if (!fixedVideoModeRef.current) return
    if (stageSentinelRef.current != null) return // já armado
    const tick = () => {
      stageSentinelRef.current = null
      if (stageRafRef.current == null && stageVisibleRef.current) {
        syncStageToContainer()
      }
      stageSentinelRef.current = setTimeout(tick, STAGE_SENTINEL_MS)
    }
    stageSentinelRef.current = setTimeout(tick, STAGE_SENTINEL_MS)
  }, [syncStageToContainer])

  /**
   * Dispara (ou estende) um burst de re-medição por rAF.
   *
   * `expectMovement` = "este gatilho PROMETE que o slot vai se mexer" (só o
   * cruzamento da zona da nav promete isso). Com ele o burst não desiste antes de
   * ver o primeiro movimento — o prazo fixo de 500ms do corte anterior vencia
   * antes disso em aparelho lento. Sem ele, o burst só acompanha o assentamento
   * e para no silêncio geométrico.
   *
   * Rearme (chamada com um burst já em voo): reseta o relógio e MANTÉM
   * `sawMovement` — o movimento visto nesta janela continua valendo. Burst NOVO
   * zera `sawMovement`, senão um `true` velho faria a política pular direto pro
   * critério de silêncio e desistir cedo, que é o defeito.
   */
  const startStageBurst = useCallback(
    (expectMovement = false) => {
      if (!fixedVideoModeRef.current) return
      // O sentinela é armado por TODO caminho de mostrar (este é o funil comum de
      // `reconcileStage` e do `attachTo`), pra que um `stopStageTracking` anterior
      // não deixe a rede desligada.
      armStageSentinel()
      const agora = performance.now()
      burstStartAtRef.current = agora
      if (expectMovement) burstExpectMovementRef.current = true
      if (stageRafRef.current != null) return // já em burst
      burstSawMovementRef.current = false
      burstLastChangeAtRef.current = agora
      const frame = () => {
        const { hasContainer, changed } = syncStageToContainer()
        const t = performance.now()
        if (changed) {
          burstSawMovementRef.current = true
          burstLastChangeAtRef.current = t
        }
        const continua =
          hasContainer &&
          burstShouldContinue({
            elapsedMs: t - burstStartAtRef.current,
            msSinceLastChange: t - burstLastChangeAtRef.current,
            sawMovement: burstSawMovementRef.current,
            expectMovement: burstExpectMovementRef.current,
          })
        if (continua) {
          stageRafRef.current = requestAnimationFrame(frame)
        } else {
          stageRafRef.current = null
          burstExpectMovementRef.current = false
        }
      }
      stageRafRef.current = requestAnimationFrame(frame)
    },
    [armStageSentinel, syncStageToContainer],
  )

  /**
   * Aplica o invariante: mostra o stage se `stageShouldShow()`, esconde se não.
   * É o ÚNICO caminho que escreve visibilidade — nenhum caller de lifecycle
   * chama `hideStage`/`showStageAt` direto.
   *
   * ⚠️ NÃO é de graça, e uma versão anterior deste bloco dizia que era ("barato,
   * 2 leituras de ref; pode ser chamado de qualquer transição sem pensar"). No
   * ramo de MOSTRAR ele:
   *   - lê `getBoundingClientRect` do container (FORÇA REFLOW) e reaplica o
   *     estilo do stage, porque zera `lastStageKeyRef` de propósito;
   *   - abre um burst de rAF (que dura até o silêncio geométrico, teto
   *     `STAGE_BURST_CEILING_MS`) e ARMA o sentinela;
   *   - MEXE NO PLAYBACK: `muted = false` e `play()` se pausado.
   *
   * O ramo de esconder é barato. Então: chame de transições de LIFECYCLE (carga,
   * suspend, destroy) e de eventos ESPARSOS (resize, cruzamento da zona de foco)
   * — nunca por frame, nunca dentro do burst (o burst usa
   * `syncStageToContainer`, que tem change-detection), e nunca de um caminho que
   * não queira tocar em áudio.
   */
  const reconcileStage = useCallback((expectMovement = false): void => {
    if (!fixedVideoModeRef.current) return
    if (stageShouldShow()) {
      // Força a reaplicação: a chave velha pode descrever o rect de antes da
      // transição (o slot pode ter se movido enquanto o stage estava escondido).
      lastStageKeyRef.current = ''
      syncStageToContainer()
      startStageBurst(expectMovement)
      // MOSTRAR É GEOMETRIA + PLAYBACK, nunca só geometria. O `doSuspend` deixa
      // o <video> `muted` (mute defensivo) e pausado; devolver o retângulo sem
      // desfazer isso entrega um vídeo mudo e parado na tela — o mesmo quadro
      // de "canal indisponível". O `stageShouldShow` já barra o caso sem mídia
      // armada; isto aqui é a outra metade, e espelha o que o `attachTo` faz ao
      // pousar o vídeo num slot. O `play()` vai sem tratamento de gesto de
      // propósito: quem arma o gesture-retry é o `attachTo` (é dele o contexto
      // de interação do usuário); aqui um autoplay bloqueado só não retoma.
      const v = videoRef.current
      if (v) {
        v.muted = false
        if (v.paused) void v.play().catch(() => undefined)
      }
    } else {
      stopStageTracking()
      hideStage()
    }
  }, [hideStage, stageShouldShow, startStageBurst, stopStageTracking, syncStageToContainer])

  // Re-ancora quando o foco CRUZA a zona nav↔conteúdo (o invariante do movimento
  // do slot), não por tipo de tecla. O Norigin NÃO emite evento de foco, então
  // seguimos o padrão do useFocusKey: poll barato via rAF de getCurrentFocusKey
  // (getter síncrono, SEM reflow) e, só no CRUZAMENTO (crossedNavZone), armamos
  // o burst — que aí sim mede o rect. Assim pegamos seta vertical, BACK
  // hierárquico (setFocus('nav-live') sem seta) e setFocus('nav-*') programático,
  // sem furo; e o surfe horizontal (chip→chip, card→card) NÃO cruza a zona → não
  // arma → zero getBoundingClientRect por frame no surfe (o ganho de perf fica de
  // pé). Só re-ancora se há algo attached. Custo ocioso: 1 getCurrentFocusKey por
  // frame — idêntico ao useFocusKey que a TopNav já roda o tempo todo.
  useEffect(() => {
    if (!fixedVideoModeRef.current) return
    let raf = 0
    let alive = true
    const readKey = (): string => {
      try {
        return getCurrentFocusKey() || ''
      } catch {
        return ''
      }
    }
    lastNavFocusKeyRef.current = readKey()
    const tick = () => {
      if (!alive) return
      const next = readKey()
      // Cruzou nav↔conteúdo → a TopNav vai colapsar/expandir → o slot do Hero
      // move. Abre a janela de burst pra o <video> seguir na animação (só se há
      // algo attached; fora da live é no-op). lastNavFocusKey acompanha sempre.
      if (crossedNavZone(lastNavFocusKeyRef.current, next)) {
        // Passa pela DECISÃO, não direto pro burst: se o invariante mudou desde
        // a última aplicação (consumidor desconectado, mídia suspensa, saímos da
        // live), reconciliar esconde; se segue válido, ele reposiciona e abre o
        // burst. Chamar `startStageBurst` direto só PARAVA o rastreio quando o
        // invariante caía — e parar não é esconder: o stage ficava com a
        // geometria velha, visível, sobre uma tela que já não é dele.
        // `true` = este gatilho PROMETE movimento do slot. É o que impede o burst
        // de desistir antes de o React re-renderizar a TopNav (até ~1,7 s no tier
        // low, medido nesta casa — ver TopNav.tsx).
        reconcileStage(true)
      }
      lastNavFocusKeyRef.current = next
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    const onResize = () => reconcileStage()
    window.addEventListener('resize', onResize)

    // Fim da transição da TopNav = o slot ACABOU de assentar na posição final.
    // Sinal exato e barato, complementar ao burst (que é amostragem).
    //
    // ⚠️ NÃO CONTE COM ELE NO APARELHO ONDE O BUG FOI MEDIDO: `--transition-base`
    // é 0ms nos tiers vlow/low (`packages/ui/src/tokens.css`), e transição de
    // duração zero NÃO dispara `transitionend`. Ou seja, exatamente nos aparelhos
    // mais lentos — os que mais demoram pra re-renderizar a TopNav — este fio não
    // existe, e quem sustenta a convergência é o SENTINELA. Isto aqui é ganho de
    // latência nos tiers mid/high, não a rede de segurança.
    //
    // ⚠️ Terceiro argumento BOOLEANO: `transitionend` não borbulha até `document`
    // de forma confiável no motor antigo, então capturamos — e o overload de
    // objeto (`{capture:true}`) é Chr49+, mais novo que o nosso piso (Chr38).
    //
    // ⚠️ Filtro por `tagName`, NUNCA por `propertyName`: a TopNav transiciona
    // `max-height`/`padding`/`transform`/`opacity` juntos, e amarrar o provider à
    // propriedade que ELA escolheu seria acoplamento por detalhe — trocar a
    // propriedade no CSS quebraria o rastreio em silêncio.
    //
    // ⚠️ Só REPOSICIONA. Nada de `reconcileStage` aqui: ele mexe em playback
    // (`muted`, `play()`), e isto dispara em toda transição de menu.
    const onTransEnd = (e: Event) => {
      const alvo = e.target as HTMLElement | null
      if (!alvo || alvo.tagName !== 'NAV') return
      syncStageToContainer()
      startStageBurst()
    }
    document.addEventListener('transitionend', onTransEnd, true)

    // A rede de convergência tem que existir desde o mount, não só depois do
    // primeiro caminho de mostrar.
    armStageSentinel()

    return () => {
      alive = false
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('transitionend', onTransEnd, true)
      if (stageSentinelRef.current != null) {
        clearTimeout(stageSentinelRef.current)
        stageSentinelRef.current = null
      }
    }
  }, [armStageSentinel, reconcileStage, startStageBurst, syncStageToContainer])

  const clearStalled = useCallback(() => {
    if (stalledTimerRef.current) {
      clearTimeout(stalledTimerRef.current)
      stalledTimerRef.current = null
    }
  }, [])

  const clearStabilize = useCallback(() => {
    if (stabilizeTimerRef.current) {
      clearTimeout(stabilizeTimerRef.current)
      stabilizeTimerRef.current = null
    }
  }, [])

  const clearFreezeWatch = useCallback(() => {
    if (freezeWatchRef.current) {
      clearInterval(freezeWatchRef.current)
      freezeWatchRef.current = null
    }
  }, [])

  // Limpa AMBOS os watchdogs de abertura (FASE 1 + FASE 2) de uma vez — todos os
  // call sites (playing/ready/error/suspend/destroy/novo doLoad) querem zerar os
  // dois juntos, então centralizar aqui evita esquecer um.
  const clearInitialLoad = useCallback(() => {
    if (initialLoadTimerRef.current) {
      clearTimeout(initialLoadTimerRef.current)
      initialLoadTimerRef.current = null
    }
    if (openFailoverTimerRef.current) {
      clearTimeout(openFailoverTimerRef.current)
      openFailoverTimerRef.current = null
    }
  }, [])

  // fix/tv-provider-unavailable-no-storm — cancela o timer de back-off de
  // PROVEDOR-FORA pendente. Chamado no início de todo doLoad (um load novo
  // supersede a re-tentativa) e no destroy.
  const clearProviderBackoff = useCallback(() => {
    if (providerBackoffTimerRef.current) {
      clearTimeout(providerBackoffTimerRef.current)
      providerBackoffTimerRef.current = null
    }
  }, [])

  // Ref pra doLoad — quebra dep cycle (tryAutoSwitch precisa chamar
  // doLoad, que precisa chamar tryAutoSwitch via err handler).
  // F3 — 4º arg `avoidEndpointId`: quando presente, o sign deste load pede ao
  // backend pra EVITAR esse endpoint e resolver o próximo (failover de endereço).
  const doLoadRef = useRef<
    | ((
        channelId: string,
        group: GroupedChannel,
        keepAttempted?: boolean,
        avoidEndpointId?: string,
        providerRetry?: boolean,
      ) => void)
    | null
  >(null)

  // F3 — ref pro tryEndpointFailover (mesmo motivo do doLoadRef: quebra o ciclo
  // tryAutoSwitch → tryEndpointFailover → doLoad → tryAutoSwitch).
  const tryEndpointFailoverRef = useRef<((reason: AutoSwitchReason) => boolean) | null>(null)

  // ===== Instrumentação do caminho crítico até o 1º FRAME =====
  // O caminho da TV ao Vivo (LiveHero) era CEGO: PlayerScreen, SeriesPlayer e
  // CatchupPlayer reportam `ttff`, mas o hero — que é o primeiro vídeo que o
  // usuário vê ao abrir a Live, e o caminho de que ele reclama — não reportava
  // nada. Sem isto, toda leva de perf aqui discute por dedução.
  //
  // Marcos: zap (doLoad) → sign → player.load() → 'playing' (1º frame).
  // O total vira `ttff` (evento que já existe e já vai pro painel); os
  // segmentos viram performance.measure, legíveis por CDP/device-trace no lab
  // sem custo de rede. Mede-se do zap, não do mount: é o que o usuário sente.
  const zapStartRef = useRef<number | null>(null)
  const signDoneRef = useRef<number | null>(null)
  const playerLoadAtRef = useRef<number | null>(null)
  const ttffReportedRef = useRef<string | null>(null)

  /**
   * Tenta a próxima source do grupo atual. Retorna true se trocou,
   * false se esgotou. Comum entre erro inicial, runtime, stalled, sign.
   *
   * Política manual (decisão Fernando 2026-05-14): "Falhou = volta pro
   * auto-switch". Antes a trava era cega (`if (isManualActive()) return
   * false`) — bloqueava QUALQUER rotação por 30min após user escolher
   * fonte, mesmo se essa fonte falhasse imediatamente. Sintoma original
   * (bug "Discovery Channel 4K"): user volta ao app, fonte preferida
   * indisponível → "Tentar de novo" prematuro sem rotação automática.
   *
   * `tryAutoSwitch` SÓ é chamado em resposta a falha real (initial-error,
   * runtime-error, stalled, sign). Se chegamos aqui, algo já quebrou —
   * rotacionar é o desejado, mesmo com manual ativo. A escolha manual
   * persiste pra PRÓXIMA abertura do canal (via `getManualSource` no IDB),
   * mas dentro de uma cadeia de falhas, manual não trava o fallback.
   */
  const tryAutoSwitch = useCallback(
    (reason: AutoSwitchReason): boolean => {
      const cur = stateRef.current
      if (!cur.group || !cur.channelId) return false

      attemptedSourcesRef.current.add(cur.channelId)

      let candidate = nextSource(cur.group, cur.channelId)
      while (
        candidate &&
        attemptedSourcesRef.current.has(candidate.channelId)
      ) {
        candidate = nextSource(cur.group, candidate.channelId)
      }
      if (!candidate) {
        candidate =
          cur.group.sources.find(
            (s) => !attemptedSourcesRef.current.has(s.channelId),
          ) ?? null
      }
      if (!candidate) {
        // F3 — esgotou as FONTES do canal neste endpoint. Antes de desistir,
        // tenta UMA rodada de PRÓXIMO ENDEREÇO do provider: re-sign com
        // avoidEndpointId = endpoint atual. Se o canal só tem 1 fonte (comum em
        // catálogos pequenos) ou todas as fontes vivem no mesmo endpoint
        // degradado, é a única saída. Espelha o auto-switch de família, só que
        // a "próxima opção" é resolvida pelo backend por priority.
        if (tryEndpointFailoverRef.current?.(reason) === true) {
          // Disparou re-sign no novo endereço. As fontes do canal podem
          // funcionar lá, então reseta o set de fontes (mas mantém o de
          // endereços, que é o anti-loop desta cadeia).
          attemptedSourcesRef.current = new Set()
          return true
        }
        // Esgotou fontes E endereços. Telemetria pra dashboard de qualidade
        // (canais com taxa alta = provider degradado ou todos os streams
        // indisponíveis). P1 #4 do review auto-switch.
        track({
          type: 'auto_switch_exhausted',
          groupKey: cur.group.key,
          lastChannelId: cur.channelId,
          attempts: attemptedSourcesRef.current.size,
          ts: Math.floor(Date.now() / 1000),
        })
        // P1 #3 do review (política B): se havia manual salvo e tudo
        // esgotou, apaga o registro — fonte preferida não funcionou.
        // Próxima abertura tenta sources[0] normal. Não precisa esperar
        // — fire-and-forget, log silencioso se falhar.
        void clearManualSource(cur.group.key)
        return false
      }
      // Telemetria do switch (P1 #4 do review): permite identificar canais
      // ruins, validar a feature em prod, e correlacionar com player_error.
      track({
        type: 'auto_switch',
        reason,
        groupKey: cur.group.key,
        fromChannelId: cur.channelId,
        toChannelId: candidate.channelId,
        attemptIdx: attemptedSourcesRef.current.size,
        ts: Math.floor(Date.now() / 1000),
      })
      fanOut('autoSwitch', reason)
      // Recarrega no mesmo grupo, channelId novo. Mantém attempted set
      // (pra próximas tentativas — só reseta em troca de grupo).
      doLoadRef.current?.(candidate.channelId, cur.group, /* keepAttempted */ true)
      return true
    },
    [fanOut],
  )

  /**
   * F3 — failover de ENDEREÇO do provider. Chamado por tryAutoSwitch SÓ depois
   * de esgotar as fontes do canal no endpoint atual. Pede ao backend o PRÓXIMO
   * endereço via re-sign com `avoidEndpointId = endpoint atual` e re-roda doLoad
   * com ele. Retorna true se disparou a rodada, false se não há para onde ir.
   *
   * Anti-loop (espelho do attemptedSourcesRef, crítico):
   * - `attemptedEndpointsRef` acumula os endpointIds já tentados nesta cadeia.
   *   Se o endpoint atual já está lá, NÃO re-tenta (backend não tem mais novos).
   * - `MAX_ENDPOINT_FAILOVER_ATTEMPTS` é o teto absoluto de rodadas — terminação
   *   garantida mesmo num backend patológico.
   * - O endpointId DEVOLVIDO pelo novo sign é checado em doLoad: se já foi
   *   tentado, paramos (erro normal), nunca loop infinito de re-sign.
   *
   * Degrada gracioso: se o sign atual não trouxe endpointId (backend antigo,
   * retrocompat), lastEndpointIdRef é null → não há o que evitar → retorna false
   * e o fluxo cai no auto_switch_exhausted normal.
   */
  const tryEndpointFailover = useCallback(
    (reason: AutoSwitchReason): boolean => {
      const cur = stateRef.current
      if (!cur.group || !cur.channelId) return false
      // Decisão pura (testada em endpoint-failover.test.ts): vale a pena pedir o
      // próximo endereço, ou já esgotamos? Anti-loop mora aqui.
      const decision = decideEndpointFailoverStart({
        lastEndpointId: lastEndpointIdRef.current,
        attemptedEndpointIds: attemptedEndpointsRef.current,
        attemptCount: endpointFailoverCountRef.current,
        maxAttempts: MAX_ENDPOINT_FAILOVER_ATTEMPTS,
      })
      if (decision.type === 'stop') return false

      const avoid = decision.avoidEndpointId
      attemptedEndpointsRef.current.add(avoid)
      endpointFailoverCountRef.current += 1
      track({
        type: 'endpoint_failover',
        reason,
        groupKey: cur.group.key,
        channelId: cur.channelId,
        avoidEndpointId: avoid,
        attemptIdx: endpointFailoverCountRef.current,
        ts: Math.floor(Date.now() / 1000),
      })
      fanOut('autoSwitch', reason)
      // Re-sign forçando o próximo endereço. keepAttempted=true: doLoad não
      // mexe nos sets (a reentrada controla o reset de fontes; os endereços
      // tentados têm que persistir pra o anti-loop valer).
      doLoadRef.current?.(cur.channelId, cur.group, /* keepAttempted */ true, avoid)
      return true
    },
    [fanOut],
  )

  // Publica tryEndpointFailover no ref (quebra o ciclo com tryAutoSwitch/doLoad).
  useEffect(() => {
    tryEndpointFailoverRef.current = tryEndpointFailover
  }, [tryEndpointFailover])

  // Arma listener global que retoma o play do canal atual no primeiro
  // gesto do user. Idempotente — múltiplas chamadas substituem o waiter
  // anterior (sempre só 1 handler pendente). Cleanup ao destruir o player.
  const armGestureRetry = useCallback(() => {
    if (typeof window === 'undefined') return
    if (gestureWaiterRef.current) return // já tem 1 armado
    const retry = () => {
      window.removeEventListener('pointerdown', retry, true)
      window.removeEventListener('keydown', retry, true)
      window.removeEventListener('touchstart', retry, true)
      gestureWaiterRef.current = null
      const cur = stateRef.current
      if (!cur.channelId || !cur.group) return
      // Re-load: reentra em doLoad (play do <video> precisa rodar dentro
      // do gesture handler — `attachTo` retoma quando attachMode != null).
      const v = videoRef.current
      if (v && v.paused) {
        v.play().catch(() => {
          // Se ainda falha (bug, race), armar novamente.
          armGestureRetry()
        })
      }
    }
    gestureWaiterRef.current = retry
    window.addEventListener('pointerdown', retry, true)
    window.addEventListener('keydown', retry, true)
    window.addEventListener('touchstart', retry, true)
  }, [])

  const disarmGestureRetry = useCallback(() => {
    if (typeof window === 'undefined') return
    const retry = gestureWaiterRef.current
    if (!retry) return
    window.removeEventListener('pointerdown', retry, true)
    window.removeEventListener('keydown', retry, true)
    window.removeEventListener('touchstart', retry, true)
    gestureWaiterRef.current = null
  }, [])

  /**
   * Fecha a medição do caminho crítico no 1º frame da Live.
   *
   * `ttff` (total zap→frame) vai pro painel como os outros players já fazem.
   * Os segmentos ficam como performance.measure — dão o recorte
   * (rede+sign vs demux/mpegts) pra quem trace o device, sem custo em campo.
   *
   * Idempotente por canal: 'playing' re-dispara em unpause/recover, e sem o
   * guard o "ttff" viraria um número inventado a cada volta de buffering.
   */
  const reportLiveTtff = useCallback((): void => {
    const zapAt = zapStartRef.current
    const channelId = stateRef.current.channelId
    if (zapAt === null || !channelId) return
    if (ttffReportedRef.current === channelId) return
    ttffReportedRef.current = channelId

    const now = performance.now()
    track({
      type: 'ttff',
      ms: Math.round(now - zapAt),
      channelId,
      title: stateRef.current.group?.displayName || undefined,
      ts: Math.floor(Date.now() / 1000),
    })

    // Segmentos (lab): NÃO vão pra rede. Timeline via measure L2 (lido por CDP) +
    // ring buffer em window (leitura direta no device). A lógica pura vive em
    // live-ttff.ts e é TESTADA (a telemetria já morreu SILENCIOSA no Chr53 — a
    // forma L3 `measure(name,{start,end})` lançava e era engolida; o teste trava
    // isso). Marks de zap/sign/load foram criados em doLoad; aqui fechamos no frame.
    recordLiveTtff({
      channelId,
      zapAt,
      signAt: signDoneRef.current,
      loadAt: playerLoadAtRef.current,
      now,
    })
  }, [])

  const ensurePlayer = useCallback((): MediaPlayer => {
    if (playerRef.current) return playerRef.current
    // Player config tier-aware: vlow/low pré-buffer 10s + ABR baixo;
    // mid+ pré-buffer 30s + ABR alto. cpuTier capability respeita
    // override do user (Settings) e snapshot dos signals — bootstrap.ts
    // dá `await initSignals()` antes do React montar (passo 5), então
    // este `getCapability` síncrono pega valor real, não fallback.
    const tier = getCapability('cpuTier')
    const player = createPlayer(tier)
    if (videoRef.current) player.attach(videoRef.current)

    // Wire interno: traduz events do MediaPlayer pra subscribers + state.
    player.on('ready', () => {
      // Por que `<video>.play()` em vez de `player.play()`:
      // O <video> element é PERSISTENTE (vive em portal, sobrevive
      // doDestroy/recreate). Browser preserva gesture-permission por
      // elemento. Chamar diretamente o videoEl reusa a permission
      // adquirida na primeira interação do user (login/navegação).
      // player.play() em alguns paths cria nova "activation chain" e
      // pode perder o gesto. AbortError aqui é race interno (load nova
      // sobrescrevendo) — auto-retry resolve.
      const v = videoRef.current
      // Live tocando = ÁUDIO LIGADO (decisão de produto; o <video> já nasce
      // com muted={false}). O mute defensivo do `doSuspend` é o único que
      // liga muted=true — e ele não pode sobreviver até um play() novo.
      // Desfazer aqui é o cinto-e-suspensório do fix do route guard: mesmo
      // que algum suspend dispare fora de hora, o play seguinte volta com som.
      // Genérico: no web isso é no-op (lá muted já é false).
      // Só com mídia ARMADA (ver `tryPlay` abaixo): um 'ready' que chega depois
      // de um suspend não desfaz o silêncio que o suspend acabou de impor.
      if (v && mediaArmedRef.current) v.muted = false
      const tryPlay = (attempt: number): void => {
        if (!v) return
        // Só toca com mídia ARMADA — o estado próprio e SÍNCRONO de "há carga
        // viva neste <video>" (true no corpo do `doLoad`, false no `doSuspend`
        // e no `doDestroy`). Dois defeitos o guard anterior deixava passar:
        //  1. Ele lia `stateRef.current.channelId`, que só assenta num efeito
        //     PÓS-COMMIT. Depois de um destroy o canal é `null`; na recarga
        //     seguinte, com o sign vindo do cache (`signStreamDeduped` devolve
        //     `Promise.resolve` num hit <45s) e o `runLoad` do native-ts todo em
        //     microtask, este 'ready' roda ANTES de o React commitar o
        //     `status:'loading'` — e o play() nunca acontecia: `src` posto,
        //     `<video>` parado. É exatamente a volta do player de overlay.
        //  2. Um 'ready' que chega DEPOIS de um suspend (a carga já estava na
        //     fila do player quando a rota saiu): o `channelId` segue preenchido
        //     no suspend, então o guard deixava tocar por baixo da tela nova.
        // A troca de canal ("outra doLoad já trocou") não depende deste guard:
        // o WebPlayer aborta o `runLoad` superado pelo token antes de emitir
        // 'ready', e o singleton reusa UMA instância.
        if (!mediaArmedRef.current) return
        v.play().catch((err: Error) => {
          if (err.name === 'AbortError' && attempt < 2) {
            // Race com nova load: tenta de novo no próximo tick.
            // Limite 2 attempts pra não loopar se algo estiver mesmo errado.
            if (import.meta.env.DEV) {
              console.warn('[live-player] play() aborted, retry', attempt + 1)
            }
            setTimeout(() => tryPlay(attempt + 1), 50)
            return
          }
          if (err.name === 'NotAllowedError') {
            // Primeira sessão web SEM gesture prévio (raro: login passou
            // por click, mas se app foi aberto via deep-link sem
            // interação, cai aqui). Volta pra idle + arma gesture retry.
            if (import.meta.env.DEV) {
              console.warn('[live-player] autoplay blocked, waiting for gesture')
            }
            setState((s) => ({ ...s, status: 'idle', error: null }))
            armGestureRetry()
            return
          }
          if (err.name === 'AbortError') {
            // Esgotou retries — silencia (próxima doLoad vai resolver).
            return
          }
          // Outro erro: re-emite via fanOut (caller pode tratar).
          fanOut('error', {
            code: err.name,
            message: err.message,
            fatal: false,
          })
        })
      }
      tryPlay(0)
      fanOut('ready')
    })
    player.on('playing', () => {
      // 1º FRAME = fim do caminho crítico de rede. Libera a telemetria que
      // estava represada desde o sign (net-priority). Sem isto, ela só sairia
      // no teto do hold — e, antes desta leva, saía DENTRO do buffering.
      notifyPlaybackStarted()
      reportLiveTtff()
      const v = videoRef.current
      // Default "TEM VÍDEO": a decisão vem do demuxer (mediaHasVideoRef, do
      // mpegts MEDIA_INFO), NUNCA de `video.videoWidth` (racy — no `playing` o
      // áudio já bufferizou mas o 1º keyframe pode não ter decodado). Se o
      // MEDIA_INFO chegar DEPOIS do playing, o handler 'mediainfo' corrige.
      setState((s) => ({
        ...s,
        status: statusOnPlaying(mediaHasVideoRef.current),
        error: null,
        notice: null,
      }))
      hasPlayedRef.current = true
      // fix/tv-provider-unavailable-no-storm — tocou: provedor voltou. Zera a
      // cadeia de back-off (próxima queda recomeça do zero) e mata timer órfão.
      providerUnavailableRetriesRef.current = 0
      clearProviderBackoff()
      clearStalled()
      clearInitialLoad() // P3 #9: chegou a tocar, watchdog inicial não precisa
      // Reset attempted após 30s de playback estável: se voltar a falhar,
      // permite retentar fontes que tinham falhado antes (pode ter sido
      // glitch transitório).
      clearStabilize()
      stabilizeTimerRef.current = setTimeout(() => {
        attemptedSourcesRef.current = new Set()
        // F3 — 30s estável = recuperação real: zera também o anti-loop de
        // endereços. Se cair de novo, o failover de endereço recomeça do zero.
        attemptedEndpointsRef.current = new Set()
        endpointFailoverCountRef.current = 0
        track({ type: 'attempted_reset', source: 'stabilize', ts: Math.floor(Date.now() / 1000) })
        // m1 review auto-switch 2026-05-11: se havia manual ativo e o
        // auto-switch trocou pra outra fonte (sem esgotar — então não
        // limpou IDB), o IDB ainda aponta pra fonte que falhou. Após
        // 30s estável na fonte nova, atualiza IDB pra refletir o que
        // está REALMENTE tocando. Próxima abertura do canal usa a fonte
        // boa, não a quebrada. Política B mantida: só atualiza se manual
        // já existia (não promove auto-pick a manual).
        const cur = stateRef.current
        if (isManualActive() && cur.group && cur.channelId) {
          void saveManualSource(cur.group.key, cur.channelId)
        }
      }, 30_000)
      // Inicia freeze watchdog: poll currentTime a cada 2s. Se ficar
      // parado > 8s sem player emitir error/buffering, trata como
      // freeze silencioso e rotaciona fontes. Casos comuns: stream
      // server caiu sem 4xx, packet loss prolongado, MSE corrompido.
      clearFreezeWatch()
      lastVideoTimeRef.current = v?.currentTime ?? 0
      lastAdvancedAtRef.current = Date.now()
      freezeWatchRef.current = setInterval(() => {
        const vid = videoRef.current
        if (!vid) return
        const now = vid.currentTime
        if (now > lastVideoTimeRef.current + 0.05) {
          lastVideoTimeRef.current = now
          lastAdvancedAtRef.current = Date.now()
          return
        }
        // currentTime parado. Quanto tempo?
        const stuckMs = Date.now() - lastAdvancedAtRef.current
        if (stuckMs > 8_000) {
          clearFreezeWatch()
          // Se offline, não rotaciona fonte (não adianta) — espera voltar.
          if (typeof navigator !== 'undefined' && !navigator.onLine) return
          tryAutoSwitch('stalled')
        }
      }, 2_000)
      fanOut('playing')
    })
    // Verdade do demuxer (mpegts MEDIA_INFO): guarda `hasVideo` do canal atual
    // e SÓ marca audio-only quando ele CONFIRMA ausência de vídeo (rádio real).
    // Cobre as duas ordens de chegada: se o MEDIA_INFO vier ANTES do 'playing',
    // o ref já está setado e o 'playing' lê `false` → audio-only; se vier
    // DEPOIS (o 'playing' default marcou 'playing'), aqui corrigimos pra
    // audio-only. `hasVideo` true/desconhecido nunca esconde vídeo (erra pra
    // tem-vídeo). Não toca em 'idle'/'error' (canal não carregado / falho).
    player.on('mediainfo', (info) => {
      mediaHasVideoRef.current = info.hasVideo
      setState((s) => {
        const next = statusOnMediaInfo(info.hasVideo, s.status)
        return next === s.status ? s : { ...s, status: next }
      })
    })
    player.on('buffering', () => {
      fanOut('buffering')
      // Watchdog stalled: se ficar 15s em buffering sem voltar pra
      // playing, considera stream travado e troca fonte.
      clearStalled()
      // Freeze watchdog pausa enquanto buffering (event normal); só
      // dispara quando NÃO houver buffering event (freeze silencioso).
      clearFreezeWatch()
      if (hasPlayedRef.current) {
        stalledTimerRef.current = setTimeout(() => {
          tryAutoSwitch('stalled')
        }, 15_000)
      }
    })
    player.on('error', (err) => {
      // Mídia DESARMADA (suspensa pela rota ou pelo overlay, ou destruída): este
      // erro não é falha do canal — é o <video> sendo esvaziado de propósito, ou
      // resto do pipeline que acabou de ser mandado parar. Tratá-lo como falha
      // rodava o `tryAutoSwitch` com o canal PARADO: queimava a fonte boa (vai
      // pro `attempted`), anunciava `autoSwitch` e recarregava a RESERVA por
      // baixo da tela nova — no native-ts, ocupando o decodificador. Pelo spec,
      // `removeAttribute('src')` + `load()` não dispara `error`; a engine do
      // aparelho e os ramos que passam a ouvir o `error` do <video> (HLS nativo,
      // mp4) não são o spec. O suspend da ROTA não destrói a instância (o
      // `acceptEvents` do player segue true), então o player não barra sozinho.
      // Estado próprio e SÍNCRONO (true no corpo do `doLoad`, false no
      // `doSuspend`/`doDestroy`). Gate: `overlay-suspend.dom.test.tsx`, "ROTA: um
      // error do <video> no instante do suspend".
      if (!mediaArmedRef.current) return
      // Bloqueio geo/ToS: detectado pelo STATUS DO GET real do stream, que o
      // player agora propaga (`httpStatus`). Substitui o `fetch HEAD`
      // bloqueante que rodava antes de todo load. Mesmos códigos que o
      // pre-check antigo tratava — e a UI segue lendo `code === 'blocked'`
      // (PlayerScreen/MobilePlayer) sem mudar nada.
      const isBlocked = isBlockedStatus(err.httpStatus)
      const errPayload: LivePlayerError = isBlocked
        ? { code: 'blocked', message: `blocked_${err.httpStatus}`, fatal: false }
        : { code: err.code, message: err.message, fatal: err.fatal }
      // QoE: track player_error pra dashboards de qualidade.
      const cur = stateRef.current
      if (cur.channelId) {
        track({
          type: 'player_error',
          code: errPayload.code,
          channelId: cur.channelId,
          ts: Math.floor(Date.now() / 1000),
        })
      }
      clearStalled()
      clearStabilize()
      clearFreezeWatch()
      clearInitialLoad()
      // Auto-fallback central: tenta próxima source. Se trocou, mantém
      // status 'loading'. Se esgotou, propaga erro pros subscribers.
      const reason: AutoSwitchReason = isBlocked
        ? 'blocked'
        : hasPlayedRef.current
          ? 'runtime-error'
          : 'initial-error'
      const switched = tryAutoSwitch(reason)
      if (!switched) {
        setState((s) => ({ ...s, status: 'error', error: errPayload, notice: null }))
        fanOut('error', errPayload)
      }
    })

    playerRef.current = player
    return player
    // clearFreezeWatch é declarado depois (TDZ). Funcionalmente estável
    // (useCallback sem deps). Listar aqui criaria erro hoisting; comportamento
    // já correto via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armGestureRetry, clearStabilize, clearStalled, fanOut, tryAutoSwitch])

  const cancelGc = useCallback(() => {
    if (gcTimerRef.current) {
      clearTimeout(gcTimerRef.current)
      gcTimerRef.current = null
    }
  }, [])

  // ===== Parking ADIADO do <video> na layer escondida =====
  // O handoff Hero->fullscreen e fullscreen->Hero é live->live: o detach de um
  // lado e o attach do outro rodam no MESMO commit do React. Estacionar o
  // <video> na hidden layer no meio disso custa um re-parent INTEIRO a mais —
  // e no punch-through do webOS cada re-parent esvazia o elemento nativo
  // (readyState -> 0) e re-adquire o plano ÚNICO de decoder de HW (~1,7-2,3s).
  // Eram 2 re-parents por transição onde 1 basta.
  //
  // Por que ADIAR e não decidir "estou indo pra fullscreen?": a decisão por
  // destino seria PROXY (e proxy já nos custou o BACK descolando o <video> —
  // ver crossedNavZone em stage-layout.ts). O invariante da consequência é
  // "alguém vai reattachar antes do fim deste task?", e a única forma honesta
  // de responder isso é deixar o task terminar. Quem reattacha cancela; quem
  // não reattacha estaciona um tick depois, exatamente como antes.
  //
  // O ramo fixedVideoMode já fazia o equivalente ("Detach: NÃO esconde o
  // stage") — este é o mesmo princípio no ramo appendChild, que é o que roda
  // em produção hoje.
  const parkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Attach pedido ANTES de o <video> do portal existir.
  //
  // MEDIDO (probe de ordem, React 18.3, jsdom), no PRIMEIRO commit:
  //     child layoutEffect: videoRef=null   <- o consumidor pede o attach
  //     video ref callback: SET             <- só agora o <video> tem ref
  //     provider layoutEffect: videoRef=SET <- e o provider roda por último
  // Layout effects sobem de baixo pra cima, e o <video> vive num portal que é
  // IRMÃO de {children}: na primeira montagem o consumidor pede o attach antes
  // de existir o que attachar. Com effect PASSIVO isso nunca aparecia (quando o
  // flush passivo roda, todo ref já está atachado) — é exatamente o acoplamento
  // de fase que já nos custou uma vez: mudar QUANDO o código roda muda o que ele
  // enxerga. Sem esta fila, o preview do Hero simplesmente não apareceria no
  // primeiro boot da tela.
  const pendingAttachRef = useRef<{ container: HTMLElement; mode: AttachMode } | null>(null)

  const cancelPark = useCallback(() => {
    if (parkTimerRef.current !== null) {
      clearTimeout(parkTimerRef.current)
      parkTimerRef.current = null
    }
  }, [])

  /**
   * Consequência ADIADA do detach, um tick depois — cancelada por quem
   * reattachar dentro do mesmo task (handoff live→live).
   *
   * Os dois ramos têm a MESMA consequência, expressa em cada linguagem: no
   * appendChild, estacionar o <video> na hidden layer; no fixo, reconciliar a
   * visibilidade (o <video> não se move, o stage é que sai de cena). Um só
   * mecanismo de adiamento pros dois — o ramo fixo herda de graça a propriedade
   * que o adiamento existe pra dar: nada acontece no meio de um handoff.
   */
  const parkVideoNow = useCallback(() => {
    parkTimerRef.current = null
    if (fixedVideoModeRef.current) {
      reconcileStage()
      return
    }
    const video = videoRef.current
    const layer = hiddenLayerRef.current
    if (!video || !layer) return
    if (video.parentNode === layer) return
    layer.appendChild(video)
  }, [reconcileStage])

  const schedulePark = useCallback(() => {
    cancelPark()
    parkTimerRef.current = setTimeout(parkVideoNow, 0)
  }, [cancelPark, parkVideoNow])

  const scheduleGc = useCallback((delayMs: number = HIDDEN_GC_DELAY_MS) => {
    cancelGc()
    gcTimerRef.current = setTimeout(() => {
      // Timer expirou sem reattach/resume: destrói tudo. App fica leve.
      // void: doDestroy é async (espera Shaka soltar MSE); aqui não
      // temos como awaitar — fire-and-forget é OK porque ninguém precisa
      // saber quando termina. Erros são logados pelo próprio doDestroy.
      void doDestroy()
    }, delayMs)
    // doDestroy é declarado abaixo (TDZ se listado aqui). Incluí-lo via
    // refactor exigiria reordenar várias useCallbacks. Comportamento
    // correto via closure — doDestroy é estável.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelGc])

  /**
   * Re-arma o GC se o player NÃO está mais attached a nenhum container
   * (hidden layer). Usado em pontos onde `doLoad` (ou outras operações)
   * chamaram `cancelGc()` mas o load completou (ou errou) sem que
   * ninguém tenha reatachado o container — cenário concreto de
   * vazamento documentado no refator 2026-05-16.
   *
   * Por que isso é necessário:
   *
   *   1. User sai de /live pra /movies → route guard chama `doSuspend` →
   *      `scheduleGc(SUSPEND_GC_DELAY_MS=10s)` armado.
   *   2. Durante esses 10s, stream tem erro silencioso (rede, blocked) →
   *      handler de error chama `tryAutoSwitch` → `doLoad` → `cancelGc`.
   *   3. `doLoad` resolve (success ou esgotou retries) — SEM ninguém
   *      attached. Player fica tocando em hidden layer indefinidamente.
   *
   * Esta função fecha o gap: se ao final do load não há container,
   * re-arma o timer apropriado (SUSPEND quando fora de rota /live*,
   * HIDDEN quando dentro mas só sem attachment temporário).
   */
  const maybeRearmGcIfDetached = useCallback(() => {
    if (currentContainerRef.current !== null) return // alguém attached
    if (gcTimerRef.current !== null) return // já armado
    if (!playerRef.current) return // já destruído
    // Heurística pra delay: se ainda estamos em rota /live*, usar HIDDEN
    // (30s — Hero pode reattachar a qualquer momento). Se fora, usar
    // SUSPEND (10s — user navegou pra fora, intent é descartar logo).
    // inLiveRoute() já é basename-aware (empacotado file:// incluso) e nunca
    // lança — ver live-route.ts.
    scheduleGc(inLiveRoute() ? HIDDEN_GC_DELAY_MS : SUSPEND_GC_DELAY_MS)
  }, [scheduleGc])

  const doDestroy = useCallback(async (): Promise<void> => {
    cancelGc()
    clearStalled()
    clearStabilize()
    clearFreezeWatch()
    clearInitialLoad()
    // fix/tv-provider-unavailable-no-storm — mata o back-off pendente e zera a
    // cadeia: um destroy encerra qualquer re-tentativa de provedor-fora em voo.
    clearProviderBackoff()
    providerUnavailableRetriesRef.current = 0
    disarmGestureRetry()

    // Captura ref local e nullifica IMEDIATAMENTE — qualquer reentrância
    // (route guard disparando outro doDestroy, ou ensurePlayer() pré-await)
    // pega ref nula e abortta cedo. O destroy real continua awaitado.
    const player = playerRef.current
    playerRef.current = null
    // Sem player não há mídia armada — o próximo `doLoad` rearma.
    mediaArmedRef.current = false
    // Reset da janela absoluta de suspend (próximo suspend é cadeia nova).
    firstSuspendAtRef.current = null
    // native-ts: reconcilia pelo INVARIANTE. Destruir o player NÃO move o vídeo:
    // se o consumidor continua montado na live, o stage segue no slot (o
    // placeholder do Hero cobre o elemento vazio, igualzinho ao ramo appendChild,
    // onde o <video> também fica dentro do slot depois do destroy). Se ninguém
    // está attachado, o reconcile esconde e o box preto não vaza.
    //
    // ⚠️ NÃO zere `currentContainerRef` aqui. Zerar era o que fazia
    // `actions.destroy()` (trial bloqueado) e o GC esconderem o stage PARA
    // SEMPRE — o ref é o gate de todo caminho de mostrar, e só o mount do
    // consumidor o repunha. Quem escreve esse ref é o `attachTo`, e só ele.
    //
    // ESTA LINHA VALE PROS DOIS RAMOS, então vale dizer o efeito no appendChild
    // (web, mobile, TV mpegts), que é o que roda em produção hoje. Os dois
    // leitores do ref — `maybeRearmGcIfDetached` e o `checkRoute` — testam
    // `playerRef.current` ANTES, e o destroy acabou de zerá-lo: logo, no
    // instante do destroy, nada muda. A diferença aparece depois, quando um
    // `play()` recria o player com o consumidor ainda montado: antes o ref
    // mentia "ninguém attachado" e o `maybeRearmGcIfDetached` agendava um GC
    // POR BAIXO de um Hero vivo — que é exatamente o acidente de `a6888cb5`
    // ("o GC dispara e destrói o player... tela PRETA permanente na LG").
    // Agora o ref diz a verdade e o GC não é agendado. É melhora, não
    // neutralidade — e por isso está dita aqui em vez de passar como detalhe.
    // Resíduo conhecido e aceito: se um consumidor sumir SEM passar pelo
    // `attachTo(null)` (crash de árvore / RecoveryBoundary), o ref fica stale e
    // o GC só volta pelo route guard. Trocar um GC tardio por um GC que mata o
    // player debaixo do usuário é o lado certo dessa moeda.
    reconcileStage()

    // Reset de state IMEDIATO — UI percebe "idle" sem esperar o destroy
    // assíncrono terminar. Importante porque o destroy do WebPlayer agora
    // é awaitable (espera Shaka soltar MSE), pode levar dezenas de ms.
    setState((s) => ({
      status: 'idle',
      channelId: null,
      group: null,
      attachMode: null,
      error: null,
      notice: null,
      online: s.online,
    }))

    if (player) {
      // WALL 1 — barreira de recriação: publica a promise que resolve no
      // 'destroyed' (fim do teardown deste player no <video> singleton) ou
      // num timeout de segurança. Um doLoad concorrente (reopen dwell~0)
      // espera essa barreira antes de attach/load do player novo, evitando
      // que o runDestroy abaixo invalide o load novo (MediaError 4 / sign
      // preso). Publicada ANTES do await pra cobrir toda a janela do destroy.
      const barrier = createDestroyBarrier(player, DESTROY_BARRIER_TIMEOUT_MS)
      destroyBarrierRef.current = barrier
      try {
        // Await é CRÍTICO: refator 2026-05-16 do @app-iptv/player garante
        // que destroy() só resolve após Shaka/mpegts soltarem o
        // MediaSource E o <video> ter sido resetado. Sem o await, próximo
        // ensurePlayer() pode rodar attach() em cima do <video> ainda em
        // cleanup → race que causava "destroy não funciona direito".
        // Detalhes em docs/changelog/refactor-player-lifecycle.md.
        await player.destroy()
      } catch (err) {
        console.warn('[live-player] destroy error (non-fatal):', err)
      } finally {
        // Só limpa se ninguém publicou uma barreira mais nova no meio
        // (destroy encadeado). A própria promise já resolveu (destroyed ou
        // timeout), então quem estava esperando já foi liberado.
        if (destroyBarrierRef.current === barrier) destroyBarrierRef.current = null
      }
    }
  }, [cancelGc, clearFreezeWatch, clearInitialLoad, clearStabilize, clearStalled, disarmGestureRetry, reconcileStage])

  // Suspend: caminho leve pro route guard (user navegou pra fora de
  // /live ou /player). NÃO destrói o player nem reseta o <video>.src
  // — Shaka.detach + video.load() invalida gesture-permission do
  // elemento, fazendo autoplay falhar ao voltar pra Live.
  //
  // Apenas pausa + mute + agenda GC AGRESSIVO (10s). Se user voltar em
  // <10s: retomada instantânea sem reload (UX). Se ficar fora >10s:
  // GC dispara doDestroy → libera buffer Shaka, listeners, conexões.
  // Justificativa de RAM: vlow Tizen 2017 não pode ficar com 10-30s
  // de buffer MSE pendurado em background pra sempre.
  //
  // Network: Shaka para de baixar segments quando <video>.paused = true
  // (atinge bufferingGoal e fica idle). Não há vazamento de banda.
  //
  // ===== Fix #2 do refator 2026-05-16 (timer absoluto) =====
  // Antes: cada chamada de doSuspend re-armava o timer de 10s a partir
  // do AGORA. User navegando rápido (/movies → /settings → /epg, ~3s
  // cada) postergava GC indefinidamente — player suspended por minutos
  // retendo buffer MSE. Agora: `firstSuspendAtRef` marca o início do
  // primeiro suspend; chamadas subsequentes calculam delay = WINDOW −
  // elapsed, mantendo a janela absoluta de 10s desde o primeiro
  // suspend. Resume (attachTo com container) reseta o marker.
  const doSuspend = useCallback(() => {
    clearStalled()
    clearStabilize()
    clearFreezeWatch()
    clearInitialLoad()
    // fix/tv-provider-unavailable-no-storm — saímos da live: cancela o back-off
    // pendente pra NÃO disparar um re-sign em background enquanto o user está em
    // outra tela. A cadeia recomeça do zero quando ele voltar (novo doLoad).
    clearProviderBackoff()
    providerUnavailableRetriesRef.current = 0
    disarmGestureRetry()
    const v = videoRef.current
    if (v) {
      try {
        v.pause()
      } catch {
        // ignora
      }
      // Mute defensivo — antes era doDestroy que matava o áudio.
      // Suspend só pausa, mas se algum onPlaying disparar antes de
      // pausar (race), mute garante silêncio.
      v.muted = true
    }
    // ===== Fix do STREAM ZUMBI (perf/tv-zapping, medido na LG real) =====
    // `v.pause()` para o <video>, NÃO o mpegts.js. Sem `enableWorker` ele
    // segue baixando e demuxando o canal abandonado NA MAIN THREAD até o GC
    // chamar destroy() (até 10s depois). Na TV isso custou 5,5s+ SEM UM
    // FRAME de UI depois do Back, com 939 chunks do stream morto ainda
    // chegando: a tela nova não conseguia pintar porque disputava a única
    // thread com um stream que ninguém está vendo.
    //
    // `suspend()` aborta o fetch e mata o transmuxer AGORA, mas NÃO solta o
    // <video> nem destrói a instância do player — então o benefício do GC de
    // 10s (voltar rápido pro mesmo canal) continua de pé: o `attachTo`/`load()`
    // reaproveita o mesmo player singleton e o chunk já importado (sem
    // createPlayer, sem re-import). Voltar refaz a CARGA (re-sign + GET novo,
    // ~600ms até o loadstart) porque `resume()` NÃO existe — foi removido de
    // propósito (no device real não re-armava o pipeline: tela preta). O que
    // morre no suspend é só o que custa caro (rede + demux + buffer MSE).
    const p = playerRef.current
    if (p) {
      void p.suspend().catch((err: unknown) => {
        console.warn('[live-player] suspend falhou (non-fatal):', err)
      })
    }
    // A mídia deixou de estar armada: o suspend abortou o fetch e matou o
    // transmuxer, e não há `resume()`. Marcar ANTES do reconcile é o que impede
    // o stage de voltar como retângulo mudo em qualquer caminho de mostrar.
    mediaArmedRef.current = false
    // native-ts: reconcilia pelo INVARIANTE. O único caller do doSuspend é o
    // route guard no ramo `!inLiveRoute()`, então aqui o reconcile esconde — o
    // retângulo preto do <video> não vaza por cima da tela nova.
    //
    // O hack antigo (zerar `currentContainerRef` pra o burst não re-exibir o
    // stage entre o suspend e o cleanup passivo do Hero) SAIU: quem barra o
    // burst agora é o próprio `syncStageToContainer`, que consulta o invariante.
    //
    // ⚠️ E o que NÃO se promete aqui: que "o vídeo volta" se a rota voltar a
    // dizer "live" com o consumidor ainda montado. Uma versão anterior deste
    // comentário afirmava isso, e era falso — o que voltaria seria o RETÂNGULO.
    // Este suspend abortou o fetch e matou o transmuxer, e `resume()` não existe
    // (removido de propósito: no device não re-armava o pipeline). Devolver
    // geometria sem playback entrega, no punch-through, um retângulo visível sem
    // frames — o mesmo quadro de "canal indisponível" que o handoff fixo existe
    // pra não produzir. Por isso o invariante exige mídia ARMADA
    // (`mediaArmedRef`): quem devolve o stage é o `doLoad`, junto com a carga.
    reconcileStage()
    setState((s) => ({
      ...s,
      status: 'idle',
      attachMode: null,
      notice: null,
    }))
    // Marca o início do primeiro suspend da cadeia (resetado pelo
    // attachTo quando user volta).
    const now = Date.now()
    if (firstSuspendAtRef.current === null) {
      firstSuspendAtRef.current = now
    }
    const elapsed = now - firstSuspendAtRef.current
    const remaining = Math.max(0, SUSPEND_GC_DELAY_MS - elapsed)
    scheduleGc(remaining)
  }, [
    clearFreezeWatch,
    clearInitialLoad,
    clearStabilize,
    clearStalled,
    disarmGestureRetry,
    reconcileStage,
    scheduleGc,
  ])

  /**
   * Load interno — sign + player.load. Compartilhado entre play() externo
   * e tryAutoSwitch (fallback). keepAttempted=true mantém set quando
   * trocando de source dentro do mesmo grupo.
   */
  const doLoad = useCallback(
    function doLoadInner(
      channelId: string,
      group: GroupedChannel,
      keepAttempted = false,
      // F3 — quando presente, este sign pede ao backend pra EVITAR esse endpoint
      // (failover de endereço). Vem do tryEndpointFailover.
      avoidEndpointId?: string,
      // fix/tv-provider-unavailable-no-storm — true quando este load é uma
      // re-tentativa de back-off de PROVEDOR-FORA (mesma fonte). Preserva o
      // contador e o aviso calmo; um load NORMAL (zap/play/troca de fonte/
      // endpoint-failover) zera a cadeia de provedor-fora.
      providerRetry = false,
    ) {
      // ===== ESTACIONA com a pilha cobrindo o live (ver "Guard de OVERLAY") =====
      // Com uma camada por cima, nenhuma carga do live pode armar o `<video>`:
      // ninguém o vê, e com um episódio/filme tocando ela disputaria o
      // decodificador de hardware com o do player. E os chamadores não sabem do
      // overlay — a volta da rede (`online`), o failover, o back-off de provedor,
      // o consumidor remontando por baixo. Em vez de ensinar cada um, a PORTA única
      // de carga recusa, ANTES de qualquer efeito colateral (GC, stage, timers,
      // state), e guarda o pedido pra quando a pilha esvaziar. O mais novo vence: é
      // o que o consumidor quer agora. Lê a pilha na hora (`getStack`), não um
      // veredito cacheado.
      if (overlayCoversLive()) {
        overlayResumeRef.current = { channelId, group }
        return
      }
      liveRequestRef.current = { channelId, group }
      cancelGc()
      // A mídia volta a estar armada, e o stage tem que estar NO SLOT antes de
      // qualquer byte entrar no <video>. Este é o ponto que fecha o P0 do #530
      // pela raiz: no punch-through do webOS, um `load()` num elemento
      // invisível (1x1 / fora da tela) não sustenta — `readyState` fica em 0, o
      // `shouldFailoverOnOpen` rotaciona, esgota as fontes e o catálogo inteiro
      // vira "Canal indisponível". Reconciliar aqui, e não só no attach, é o que
      // garante que o elemento já esteja visível quando a carga começa.
      mediaArmedRef.current = true
      reconcileStage()
      // fix/tv-provider-unavailable-no-storm — um load NOVO supersede qualquer
      // back-off de provedor-fora pendente. Só a re-tentativa de back-off (mesma
      // fonte) preserva o contador; qualquer outro load zera a cadeia.
      clearProviderBackoff()
      if (!providerRetry) providerUnavailableRetriesRef.current = 0
      // Abre a carga no gate SÍNCRONO: invalida qualquer doLoad anterior AGORA
      // (antes de qualquer await), pra que a carga superada por um zap novo aborte
      // no 1º checkpoint em vez de escorregar pelo guard defasado do stateRef.
      // Ver load-gate.ts / loadGateRef.
      const loadToken = loadGateRef.current!.begin()
      // Marco 0 do caminho crítico: instante em que o zap começa. Reset dos
      // segmentos — este load é uma tentativa nova (inclusive em failover).
      zapStartRef.current = performance.now()
      liveMark(LIVE_MARK.zap) // A3: mark L2 pro measure Chr53-safe (reportLiveTtff)
      signDoneRef.current = null
      playerLoadAtRef.current = null
      ttffReportedRef.current = null
      // ===== Fix do GAP PRÉ-LOAD (perf/tv-zapping) =====
      // O cleanup do backend anterior só acontecia LÁ DENTRO do
      // `player.load()` — ou seja, DEPOIS do `await api.signStream`. Durante
      // todo o sign (e antes, durante o render do skeleton), o canal ANTIGO
      // seguia baixando e demuxando na main thread, competindo com o canal
      // que o usuário acabou de pedir. Medido na LG: 290-640ms de gap.
      //
      // Aqui matamos o pipeline velho de forma síncrona, no primeiro
      // instante do zap. `suspend()` entra na fila do player ANTES do
      // `load()` abaixo (a fila é FIFO), então o load segue correto — só
      // que agora o sign roda numa main thread livre e o `cleanupMpegts`
      // do runLoad encontra um pipeline já parado (destroy barato).
      const previous = playerRef.current
      if (previous) {
        void previous.suspend().catch(() => {
          // non-fatal: o load() abaixo faz o cleanup completo de qualquer jeito.
        })
      }
      // WALL 1 — a criação/attach do player NÃO acontece mais aqui: ela foi
      // adiada pra dentro da IIFE, DEPOIS da barreira de destroy. No zap
      // normal (player vivo) isso é irrelevante (ensurePlayer devolve a
      // instância existente na hora); no reopen dwell~0 (player em teardown)
      // é o que impede o attach de correr em cima do runDestroy anterior.
      // Trocou de grupo? Reset de attempted + manual (canal novo).
      const prevGroup = stateRef.current.group
      if (!keepAttempted && prevGroup?.key !== group.key) {
        attemptedSourcesRef.current = new Set()
        manualSourceAtRef.current = null
        // F3 — canal novo: zera o anti-loop de endereços e o contador de rodadas.
        attemptedEndpointsRef.current = new Set()
        endpointFailoverCountRef.current = 0
      }
      hasPlayedRef.current = false
      // Canal novo: zera a verdade do demuxer. Sem isto, o `hasVideo:false` de
      // um rádio anterior vazaria pro canal seguinte (marcaria vídeo como só
      // áudio até o novo MEDIA_INFO chegar). Volta pro default "tem vídeo".
      mediaHasVideoRef.current = null
      clearStalled()
      clearFreezeWatch()
      clearInitialLoad()

      setState((s) => ({
        ...s,
        status: 'loading',
        channelId,
        group,
        error: null,
        // Re-tentativa de back-off (mesma fonte): mantém o aviso calmo de
        // provedor-fora em tela. Load normal: limpa qualquer aviso anterior.
        notice: providerRetry ? s.notice : null,
      }))

      // P3 #9 do review: watchdog de carga inicial. Se nunca chegar a
      // 'playing' em 10s, dispara auto-switch pra próxima fonte.
      // Cobre rede ruim onde stream aceita TCP mas não entrega bytes,
      // player não emite error fatal. Sem isso, status fica em
      // 'loading' indefinidamente até timeout do player (varia muito
      // por backend — Shaka 30s+, mpegts depende).
      initialLoadTimerRef.current = setTimeout(() => {
        // Race guard SÍNCRONO: durante esses 10-20s o user pode ter trocado de
        // canal (ou outro doLoad disparou). Alinha com a FASE 1 e todos os
        // checkpoints do doLoad — usa o loadGate síncrono (loadToken) em vez do
        // stateRef, que só assenta ao final e deixaria o timer disparar switch
        // num load já superado. Sem isso, o timer dispara switch num canal que
        // nem é mais o ativo.
        if (!loadGateRef.current!.isActive(loadToken)) return
        if (hasPlayedRef.current) return // já começou a tocar, não deveria estar armado
        tryAutoSwitch('stalled')
      }, getInitialLoadTimeoutMs())

      // FASE 1 do failover da abertura (perf/tv-live-zap-failover, item 2): fonte
      // MORTA (aceita a conexão, não entrega byte) deixa o <video> preso em
      // HAVE_NOTHING. Aos ~4,5s, se NADA chegou, rotaciona já — sem esperar o
      // backstop de 10-20s. Gate por readyState (shouldFailoverOnOpen) poupa
      // fonte lenta-mas-viva (vlow+3G, já com metadata). loadToken (não o
      // stateRef defasado) garante que a carga superada por um zap não falha.
      openFailoverTimerRef.current = setTimeout(() => {
        if (hasPlayedRef.current) return
        const v = videoRef.current
        const decision = shouldFailoverOnOpen({
          isActiveLoad: loadGateRef.current!.isActive(loadToken),
          hasPlayed: hasPlayedRef.current,
          // Sem <video> = nada carregado → HAVE_NOTHING (falha na abertura).
          readyState: v ? v.readyState : 0,
        })
        if (!decision) return
        // Se offline, rotacionar não adianta (nenhuma fonte vai entregar) —
        // espera a rede voltar (o onOnline recarrega). Espelha o freeze watchdog.
        if (typeof navigator !== 'undefined' && !navigator.onLine) return
        // Fonte-única MORTA (aceita conexão, não entrega byte, sem endpoints
        // alternativos): tryAutoSwitch esgota aqui — track('auto_switch_exhausted')
        // — e devolve false SEM tocar nos timers. A FASE 2 (initialLoadTimerRef)
        // segue armada e, aos 10-20s, redispara o mesmo tryAutoSwitch esgotado →
        // conta auto_switch_exhausted EM DOBRO no dashboard de qualidade. Ao esgotar,
        // desarma ambos os timers (clearInitialLoad é idempotente) pra não duplicar
        // a telemetria. No sucesso, o doLoad da próxima fonte já re-arma tudo do zero.
        if (!tryAutoSwitch('stalled')) clearInitialLoad()
      }, getOpenFailoverMs())

      void (async () => {
        // Race-safe rearm: só re-arma se este load ainda é o ativo ao final.
        // Loads superados (substituídos por um zap/play novo) não devem re-armar
        // GC — o novo load já fez isso (ou está attached). Gate síncrono, não o
        // stateRef defasado (ver load-gate.ts).
        const isStillThisLoad = () => loadGateRef.current!.isActive(loadToken)
        try {
          // WALL 1 — serializa a recriação do player. Se um teardown está em
          // curso no <video> singleton (reopen dwell~0), espera o 'destroyed'
          // do player anterior ANTES de criar/attachar o novo. Sem destroy
          // pendente (zap normal), a ref é null e não há espera nenhuma.
          const pendingDestroy = destroyBarrierRef.current
          if (pendingDestroy) {
            await pendingDestroy
            // User pode ter zapeado durante a espera — o doLoad novo assume.
            if (!loadGateRef.current!.isActive(loadToken)) return
          }
          // Cria/attacha aqui (adiado do corpo síncrono): agora o <video> já
          // foi resetado e solto pelo runDestroy anterior — sem colisão.
          const player = ensurePlayer()
          // F3 — repassa avoidEndpointId pro backend quando este load é uma
          // rodada de failover de endereço. Sem ele, sign normal.
          const sign = await signStreamDeduped(
            'live',
            channelId,
            avoidEndpointId ? { avoidEndpointId } : {},
          )
          signDoneRef.current = performance.now()
          liveMark(LIVE_MARK.sign) // A3
          // Race guard SÍNCRONO: um zap posterior já invalidou este token
          // (aborta ANTES de abrir o GET do stream). Antes lia o stateRef
          // defasado e deixava a carga superada seguir. Ver load-gate.ts.
          if (!loadGateRef.current!.isActive(loadToken)) return
          // F3 — anti-loop do failover de endereço (crítico): se este load PEDIU
          // pra evitar um endpoint mas o backend devolveu um endpointId que JÁ
          // tentamos nesta cadeia (ou o próprio que mandamos evitar — backend sem
          // endereço novo), NÃO re-carregamos: paramos com erro normal, igual ao
          // família quando esgota. Decisão pura testada em endpoint-failover.test.ts.
          if (avoidEndpointId) {
            const verdict = decideEndpointFailoverResolved({
              avoidEndpointId,
              resolvedEndpointId: sign.endpointId ?? null,
              attemptedEndpointIds: attemptedEndpointsRef.current,
            })
            if (verdict.type === 'exhausted') {
              setState((s) => ({
                ...s,
                status: 'error',
                error: { code: 'endpoint_exhausted', message: 'no_more_endpoints', fatal: false },
              }))
              fanOut('error', {
                code: 'endpoint_exhausted',
                message: 'no_more_endpoints',
                fatal: false,
              })
              return
            }
          }
          // F3 — guarda o endpoint que ESTE sign resolveu pra próxima rodada de
          // failover saber o que evitar. null se a resposta não trouxe (backend
          // antigo) → tryEndpointFailover degrada gracioso.
          lastEndpointIdRef.current = sign.endpointId ?? null
          const fmt: 'hls' | 'mpegts' | 'mp4' =
            sign.format === 'hls'
              ? 'hls'
              : sign.format === 'mp4'
                ? 'mp4'
                : 'mpegts'
          const finalUrl = rewriteStreamUrl(sign.url, fmt, sign)
          // ===== Fix do HEAD BLOQUEANTE (perf/tv-zapping) =====
          // Aqui existia um `fetch HEAD` SEQUENCIAL, antes do load, só pra
          // detectar bloqueio geo/ToS (403/451/410/503). Ele atrasava TODA
          // troca de canal em 240-434ms na LG real — e era redundante: o
          // próprio GET do stream devolve o mesmo status, e agora o player
          // propaga esse status em `err.httpStatus` (mpegts
          // NetworkError/HttpStatusCodeInvalid; Shaka BAD_HTTP_STATUS). O
          // handler de 'error' classifica bloqueio por ali (BLOCKED_HTTP_STATUS)
          // e cai no MESMO `tryAutoSwitch('blocked')` + `code: 'blocked'` que
          // a UI já entende. Detecção mais fiel (é o status do request real,
          // não o de um HEAD que muitos painéis Xtream nem respondem igual) e
          // mais rápida (o 403 chega no primeiro byte do GET).
          //
          // Efeito colateral bom: painel Xtream com limite de 1 conexão por
          // usuário deixa de ver HEAD+GET quase simultâneos — causa conhecida
          // de 403 espúrio no zap.
          //
          // Perda aceita e consciente: o caso `x-block-reason: cloudflare-tos`
          // com HTTP 200. Bloqueio de ToS/geo não responde 200 com TS válido —
          // se responder 200 com corpo inválido, o demux falha e o auto-switch
          // roda igual (só sem o rótulo "bloqueado").
          // Último checkpoint ANTES de abrir o GET do stream: se um zap novo
          // superou este load, aborta aqui (nunca chega a segurar conexão no
          // pool minúsculo da TV). Gate síncrono, não o stateRef defasado.
          if (!loadGateRef.current!.isActive(loadToken)) return
          // Marco: entrada no player. Do zap até aqui é rede+decisão nossa;
          // daqui até 'playing' é o demux/mpegts (o bloco que ninguém media).
          playerLoadAtRef.current = performance.now()
          liveMark(LIVE_MARK.load) // A3
          await player.load({ url: finalUrl, type: fmt })
        } catch (err) {
          // P2 catch-gating — ESPELHA os checkpoints de isActive do try: se um zap
          // ou o openFailover (~4,5s) superou este load (novo loadToken) ENQUANTO o
          // sign/load estava em voo, o catch do load ANTIGO vira no-op. Sem isto, um
          // 503 mais LENTO que o openFailover desarmaria os watchdogs do load
          // SUCESSOR (clearInitialLoad) e poluiria o contador/estado de provedor-fora.
          // O finally (maybeRearmGcIfDetached) já respeita isStillThisLoad, então não
          // roda pra este load superado. Gate síncrono (loadToken), não o stateRef.
          if (!loadGateRef.current!.isActive(loadToken)) return
          const message = err instanceof Error ? err.message : 'unknown'
          // fix/tv-provider-unavailable-no-storm — distingue PROVEDOR-FORA de
          // FONTE-RUIM. Provedor-fora (503 provider_unavailable): rotacionar
          // fonte é JUSTAMENTE o que floda o edge do provedor (todas falhariam),
          // então em vez disso back-off por retryAfter e re-tenta a MESMA fonte,
          // com teto. Fonte-ruim (400 sign_failed / erro de load / rede):
          // rotaciona (comportamento antigo, legítimo — é outra fonte, não floda).
          const decision = decideSignFailure({
            err,
            retries: providerUnavailableRetriesRef.current,
          })
          if (decision.type === 'backoff') {
            track({
              type: 'provider_unavailable',
              phase: 'backoff',
              channelId,
              retryAfterSec: Math.round(decision.delayMs / 1000),
              attempt: decision.attempt,
              ts: Math.floor(Date.now() / 1000),
            })
            providerUnavailableRetriesRef.current = decision.attempt
            // CRÍTICO: desarma os watchdogs de abertura (openFailover ~4,5s +
            // initialLoad ~10-20s) armados no corpo síncrono deste doLoad. Sem
            // isto, eles disparariam `tryAutoSwitch('stalled')` durante o
            // back-off e ROTACIONARIAM as fontes — exatamente a tempestade que
            // este fix mata. O doLoad da re-tentativa re-arma tudo do zero.
            clearInitialLoad()
            // Mensagem calma (TV-first, sem alarme): segue "carregando", com o
            // aviso de provedor-fora. NÃO faz fanOut('autoSwitch') — não é troca
            // de fonte; a UI lê o aviso por playerState.notice.
            setState((s) => ({
              ...s,
              status: 'loading',
              error: null,
              notice: 'provider_unavailable',
            }))
            clearProviderBackoff()
            providerBackoffTimerRef.current = setTimeout(() => {
              // Superado por um zap durante o back-off? Aborta — o novo doLoad
              // assume. Gate síncrono (loadToken), não o stateRef defasado.
              if (!loadGateRef.current!.isActive(loadToken)) return
              doLoadRef.current?.(
                channelId,
                group,
                /* keepAttempted */ true,
                avoidEndpointId,
                /* providerRetry */ true,
              )
            }, decision.delayMs)
          } else if (decision.type === 'giveup') {
            // Estourou o teto de re-tentativas: erro calmo "tente mais tarde"
            // (o overlay mostra a mensagem específica + botão "Tentar de novo",
            // que dispara UM novo sign — nunca a rajada de 15).
            track({
              type: 'provider_unavailable',
              phase: 'giveup',
              channelId,
              retryAfterSec: 0,
              attempt: providerUnavailableRetriesRef.current,
              ts: Math.floor(Date.now() / 1000),
            })
            // Terminal: desarma os watchdogs de abertura pra não redispararem
            // rotação de fonte por cima do overlay de erro.
            clearInitialLoad()
            setState((s) => ({
              ...s,
              status: 'error',
              error: { code: 'provider_unavailable', message, fatal: false },
              notice: null,
            }))
            fanOut('error', { code: 'provider_unavailable', message, fatal: false })
          } else {
            // decision.type === 'rotate' — fonte ruim: tenta fallback (mesmo
            // padrão do PlayerScreen original).
            const switched = tryAutoSwitch('sign')
            if (!switched) {
              setState((s) => ({
                ...s,
                status: 'error',
                error: { code: 'sign_or_load_failed', message, fatal: false },
                notice: null,
              }))
              fanOut('error', { code: 'sign_or_load_failed', message, fatal: false })
            }
          }
        } finally {
          // Fix #1 do refator 2026-05-16 (vazamento background): garante
          // que se o load completou (success ou erro) E o player não está
          // mais attached a nenhum container (rodou em background via
          // tryAutoSwitch enquanto user estava em /movies), o GC seja
          // re-armado. Sem isso, player ficava tocando indefinidamente
          // em hidden layer.
          if (isStillThisLoad()) {
            maybeRearmGcIfDetached()
          }
        }
      })()
    },
    // clearFreezeWatch é estável (useCallback sem deps); listar dispara TDZ
    // se subir, e refactor de reordenação é arriscado em código de player.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cancelGc, clearStalled, ensurePlayer, fanOut, reconcileStage, tryAutoSwitch, maybeRearmGcIfDetached],
  )

  // Publica doLoad pro doLoadRef pra tryAutoSwitch poder chamar (resolve
  // dep cycle: tryAutoSwitch → doLoad → tryAutoSwitch via err handler).
  useEffect(() => {
    doLoadRef.current = doLoad
  }, [doLoad])

  // ===== API pública =====

  const play = useCallback(
    (channelId: string, group: GroupedChannel) => {
      const cur = stateRef.current
      // Dedup: lógica extraída em decidePlayAction (testada em
      // play-dedup.test.ts). NÃO inline a regra de volta sem ler
      // docs/changelog/fix-live-player-dedup-channelid-revert.md — já
      // quebrou uma vez (PR #96) por trocar channelId por group.key.
      const action = decidePlayAction({
        hasPlayer: playerRef.current !== null,
        current: { channelId: cur.channelId, status: cur.status, group: cur.group },
        requestedChannelId: channelId,
        requestedGroupKey: group.key,
      })
      if (action.type === 'noop') return

      // P1 #3 do review auto-switch: aplica manual persistido em IDB
      // se ele aponta pra source válida do grupo atual. Caller passou
      // `channelId` (geralmente sources[0] ou a fonte que clicou), mas
      // user pode ter salvado preferência diferente em sessão anterior.
      // Lookup assíncrono; redirige o play se necessário. Fallback
      // gracioso: IDB indisponível → segue caller's channelId.
      //
      // B1 do review: race guard via token. Se user zappear pra outro
      // canal enquanto o IDB lookup está pendente, o `.then` aborta —
      // sem o token, doLoad antigo clobberava o `play()` mais recente
      // (regressão crítica em zapping rápido + IDB lento em vlow Tizen).
      const playToken = Symbol('play')
      pendingPlayTokenRef.current = playToken

      // P1 (perf/tv-hero-coldstart): dispara o sign OTIMISTA em paralelo com o
      // getManualSource. Hoje o sign só começa DENTRO do `.then` do manual lookup
      // (doLoad → signStreamDeduped:1056), serializado ATRÁS do IDB read do
      // getManualSource. Como o sign já é single-flight + cache curto
      // (stream-sign.ts), aquecer aqui a chave do canal do CALLER faz o doLoad
      // colar na MESMA promise em voo no caso comum (sem manual, ou manual ==
      // caller) — o sign corre concorrente ao IDB, não depois dele.
      //   • manual == caller / sem manual (comum): doLoad reusa este sign em voo.
      //   • manual aponta pra OUTRA source (raro): doLoad assina a chave certa e
      //     este vira só cache curto (expira em <=45s, MAX_REUSE_MS). A janela
      //     crítica do sign descartado NÃO vaza: beginCritical é reentrante
      //     (net-priority.ts:155) e cancela o hold de buffering anterior.
      // INVARIANTE (refutação de ordem-de-sessão): NÃO toca streamSessionStart —
      // isto é só POST /v1/stream/sign; a sessão de trial vive no useTrialGuard
      // (eixo mount/unmount), ortogonal ao sign. Zero risco de start 2x/402/409.
      // Race de zapping continua coberto pelo pendingPlayTokenRef abaixo (o
      // doLoad certo é quem vence); um sign otimista abandonado é inócuo.
      void signStreamDeduped('live', channelId, {}, { critical: true }).catch(() => {
        // não-fatal: o doLoad refaz/aguarda o sign e trata erro no próprio catch.
      })

      void getManualSource(group.key)
        .then((manual) => {
          // Race guard: user já chamou outro play() (zapping). Abortar.
          if (pendingPlayTokenRef.current !== playToken) return
          if (!manual) {
            doLoad(channelId, group, /* keepAttempted */ false)
            return
          }
          // Manual aponta pra source que ainda existe no grupo atual?
          const valid = group.sources.some((s) => s.channelId === manual.channelId)
          if (!valid) {
            // Source sumiu (provider removeu) — apaga e usa caller's.
            void clearManualSource(group.key)
            doLoad(channelId, group, /* keepAttempted */ false)
            return
          }
          // Aplica manual: ativa TTL e carrega na fonte preferida.
          manualSourceAtRef.current = Date.now()
          doLoad(manual.channelId, group, /* keepAttempted */ false)
        })
        .catch(() => {
          // Race guard idem (errors também precisam respeitar zapping).
          if (pendingPlayTokenRef.current !== playToken) return
          // IDB indisponível (privado, cota cheia, etc) — fallback ao caller.
          doLoad(channelId, group, /* keepAttempted */ false)
        })
      return
    },
    [doLoad],
  )

  const selectSource = useCallback(
    (channelId: string) => {
      const cur = stateRef.current
      if (!cur.group) return
      // Manual: marca timestamp (auto-switch suspenso por 30min) e
      // limpa attempted (usuário pode querer revisitar fontes que tinham
      // falhado antes).
      manualSourceAtRef.current = Date.now()
      attemptedSourcesRef.current = new Set()
      // F3 — escolha manual = intenção fresca do user: zera o anti-loop de
      // endereços também, pra o failover poder recomeçar se a fonte cair.
      attemptedEndpointsRef.current = new Set()
      endpointFailoverCountRef.current = 0
      // P1 #3 do review auto-switch: persiste manual em IDB (sobrevive
      // reload). Próxima abertura do mesmo canal tenta esta fonte 1º.
      // Política B: se essa fonte esgotar todas as tentativas, registro
      // é deletado em `tryAutoSwitch` (linha de exhausted abaixo).
      void saveManualSource(cur.group.key, channelId)
      doLoad(channelId, cur.group, /* keepAttempted */ false)
    },
    [doLoad],
  )

  /** Retry "fresh": reseta attemptedSourcesRef e re-dispara doLoad no
   *  canal atual. Bug 4 (decisão Fernando 2026-05-14): "Tentar de novo"
   *  no overlay de erro precisa dar fresh start. Antes, o
   *  PlayerScreen.handleErrorRetry chamava actions.play() que, no MESMO
   *  grupo, NÃO resetava attempted (`doLoad:keepAttempted=false` só
   *  reseta quando muda de grupo) — então o retry caía direto em
   *  "esgotou" sem tentar fonte nenhuma. Aqui força. Mantém manual ref
   *  intacto (se user escolheu fonte, escolha persiste — só limpa o set
   *  de "tentadas"). */
  const retry = useCallback(() => {
    const cur = stateRef.current
    if (!cur.channelId || !cur.group) return
    attemptedSourcesRef.current = new Set()
    // F3 — "Tentar de novo" = fresh start total: zera o anti-loop de endereços
    // pra o failover poder revisitar endpoints que tinham caído.
    attemptedEndpointsRef.current = new Set()
    endpointFailoverCountRef.current = 0
    track({ type: 'attempted_reset', source: 'retry', ts: Math.floor(Date.now() / 1000) })
    doLoad(cur.channelId, cur.group, /* keepAttempted */ false)
  }, [doLoad])

  const attachTo = useCallback(
    (container: HTMLElement | null, mode: AttachMode) => {
      const video = videoRef.current
      if (!video) {
        // Ainda não há <video> (1ª montagem — ver pendingAttachRef). Enfileira
        // em vez de descartar em silêncio; o flush roda no MESMO commit.
        pendingAttachRef.current = container ? { container, mode } : null
        return
      }

      // Consumidor montando POR BAIXO da pilha de overlay (ex.: a rota volta pro
      // `/player/:id` com um episódio ainda na pilha — o `PlayerScreen` remonta
      // debaixo dele). O attach pousa o `<video>` no slot, mas NÃO desmuta nem dá
      // `play()`: o live foi silenciado e destruído pelo guard de overlay, e um
      // `play()` no elemento esvaziado põe `paused:false` sem mídia nenhuma
      // (medido no Chromium: `readyState` 0, promise pendente pra sempre). Foi o
      // `paused:false` que o smoke da LG (2.39.0) viu sob o episódio. Quem traz o
      // som e o play de volta é a carga, quando a pilha esvaziar.
      const cobertoPelaPilha = container !== null && overlayCoversLive()

      // ===== native-ts: handoff SEM re-parent (reposiciona por CSS) =====
      // NÃO faz appendChild (que esvaziaria o <video> nativo → reload ~2s). O
      // <video> fica fixo no stage; aqui só reposicionamos pro slot e (des)mutamos.
      if (fixedVideoModeRef.current) {
        if (container) {
          // Cancela a reconciliação adiada do detach que acabou de rodar neste
          // MESMO commit (Hero→fullscreen / fullscreen→Hero): sem isto o stage
          // sumiria por um tick no meio do handoff.
          cancelPark()
          currentContainerRef.current = container
          attachModeRef.current = mode
          cancelGc()
          firstSuspendAtRef.current = null
          if (!cobertoPelaPilha) video.muted = false
          // Posiciona pro slot AGORA (síncrono, sem flash) e abre um burst de
          // rAF que acompanha o slot enquanto ele assenta/anima (TopNav) — ver
          // syncStageToContainer/startStageBurst. lastStageKey reseta pra forçar
          // a 1ª aplicação (o modo pode ter mudado no handoff).
          lastStageKeyRef.current = ''
          syncStageToContainer()
          startStageBurst()
          if (!cobertoPelaPilha && video.paused) {
            video.play().catch((err: Error) => {
              if (err.name === 'NotAllowedError') {
                if (import.meta.env.DEV) {
                  console.warn('[live-player] attachTo autoplay blocked, waiting for gesture')
                }
                setState((s) => ({ ...s, status: 'idle', error: null }))
                armGestureRetry()
              }
            })
          }
        } else {
          // Detach: não esconde o stage AGORA. Hero↔fullscreen é live→live e o
          // próximo attach reposiciona no mesmo tick — esconder aqui pintaria
          // preto no meio do handoff. Agenda a reconciliação pro tick seguinte
          // (`schedulePark`): quem reattachar cancela; quem não reattachar
          // esconde, um tick depois. Mesmo mecanismo do ramo appendChild.
          currentContainerRef.current = null
          schedulePark()
          if (inLiveRoute()) {
            scheduleGc(HIDDEN_GC_DELAY_MS)
          } else {
            const firstSuspendAt = firstSuspendAtRef.current
            const elapsed = firstSuspendAt === null ? 0 : Date.now() - firstSuspendAt
            scheduleGc(Math.max(0, SUSPEND_GC_DELAY_MS - elapsed))
          }
        }
        setState((s) => ({ ...s, attachMode: mode }))
        return
      }

      // Move o <video> via DOM API (preserva playback, sem destroy).
      // appendChild remove de onde estava e coloca aqui — atomic.
      if (container) {
        // Cancela o parking adiado do detach que acabou de rodar neste MESMO
        // commit (Hero->fullscreen / fullscreen->Hero). Sem isto o <video>
        // daria a volta pela hidden layer e pagaria 2 re-parents.
        cancelPark()
        container.appendChild(video)
        currentContainerRef.current = container
        cancelGc()
        // Reset da janela absoluta de suspend — user voltou, próxima
        // cadeia de saídas começa do zero (10s cheios).
        firstSuspendAtRef.current = null
        // Desmuta ao re-attachar (suspend mutou pra silenciar áudio em
        // background fora de /live).
        // ATENÇÃO: o comentário antigo aqui dizia "em TV nativa muted nunca foi
        // setado; é no-op idempotente" — era FALSO e ajudou a esconder o bug de
        // áudio. Com o route guard quebrado no empacotado, o doSuspend rodava em
        // TODA navegação e o muted=true era bem real na TV. Guard corrigido em
        // live-route.ts; este desmute continua como rede de segurança.
        if (!cobertoPelaPilha) video.muted = false
        // Sempre que attachar (Hero OU fullscreen): se video estava
        // paused (autoplay policy, ou pause implícito ao desencaixar),
        // retoma. Aqui já houve interação do user pra trocar de tela —
        // navegar/clicar — então play() não rejeita.
        // Em TV nativa play() é no-op (já estava playing).
        if (!cobertoPelaPilha && video.paused) {
          video.play().catch((err: Error) => {
            // Mesmo padrão do `'ready'` handler — autoplay block volta
            // pra idle + arma gesture retry. AbortError = race, silencia.
            if (err.name === 'NotAllowedError') {
              if (import.meta.env.DEV) {
                console.warn('[live-player] attachTo autoplay blocked, waiting for gesture')
              }
              setState((s) => ({ ...s, status: 'idle', error: null }))
              armGestureRetry()
            }
            // AbortError ou outros: não bloqueia UI.
          })
        }
      } else {
        // Volta pra layer escondida — mantém vivo, agenda GC. ADIADO de
        // propósito: se um attach chegar antes do fim deste task (handoff
        // live->live), ele cancela e o <video> nunca dá a volta. Ver
        // schedulePark.
        schedulePark()
        currentContainerRef.current = null
        // Janela de GC coerente com a rota (mesma heurística do
        // maybeRearmGcIfDetached). Antes era SEMPRE 30s — e como o unmount da
        // PlayerScreen roda DEPOIS do route guard (popstate → checkRoute →
        // doSuspend → GC 10s; só então o React desmonta a tela), este
        // `scheduleGc()` sobrescrevia os 10s do suspend por 30s. O player
        // suspenso segurava RAM 3x mais tempo que o desenhado — em vlow Tizen
        // 2017 isso é justamente o que a janela de 10s existe pra evitar.
        if (inLiveRoute()) {
          scheduleGc(HIDDEN_GC_DELAY_MS)
        } else {
          const firstSuspendAt = firstSuspendAtRef.current
          const elapsed = firstSuspendAt === null ? 0 : Date.now() - firstSuspendAt
          scheduleGc(Math.max(0, SUSPEND_GC_DELAY_MS - elapsed))
        }
      }
      setState((s) => ({ ...s, attachMode: mode }))
    },
    [
      armGestureRetry,
      cancelGc,
      cancelPark,
      schedulePark,
      scheduleGc,
      startStageBurst,
      syncStageToContainer,
    ],
  )

  const subscribe = useCallback(
    (
      event: 'playing' | 'buffering' | 'error' | 'ready' | 'autoSwitch',
      handler: (payload?: LivePlayerError | AutoSwitchReason) => void,
    ) => {
      const subs = subscribersRef.current
      if (event === 'error') {
        const h = handler as (err: LivePlayerError) => void
        subs.error.add(h)
        return () => subs.error.delete(h)
      }
      if (event === 'autoSwitch') {
        const h = handler as (r: AutoSwitchReason) => void
        subs.autoSwitch.add(h)
        return () => subs.autoSwitch.delete(h)
      }
      const set = subs[event]
      const wrapped = handler as () => void
      set.add(wrapped)
      return () => set.delete(wrapped)
    },
    [],
  )

  const destroy = useCallback(() => {
    // Destroy PEDIDO por um consumidor (o trial bloqueado no PlayerScreen): não
    // sobra nada pra retomar. Sem isto, a saída de um player de overlay
    // recarregaria o canal que o bloqueio acabou de derrubar. O GC não passa
    // por aqui (chama `doDestroy` direto) — o dele é coleta, não recusa.
    // (O `liveRequestRef` não precisa ser zerado: depois do destroy não há player
    // nem mídia armada, e o suspend do overlay só o captura com um dos dois.)
    overlayResumeRef.current = null
    // API pública é sync (compat com callers existentes que ignoram retorno).
    // doDestroy interno é async (espera Shaka soltar MSE); fire-and-forget OK.
    void doDestroy()
  }, [doDestroy])

  // ===== Guard de OVERLAY: player de série/filme por cima do live =====
  // O route guard (mais abaixo) só enxerga ROTA. O player de série/filme deixou
  // de ser rota e virou CAMADA da pilha de overlay (`detail-overlay-store.ts`),
  // que não mexe no path — então, com a pilha sobre uma rota do live, nada
  // soltava o `<video>` deste singleton. Na LG 2019 (webOS, Chr53) isso foi
  // visto: episódio tocando e um SEGUNDO `<video>` no DOM, parado, com o `.ts` do
  // canal no `src`. O webOS tem UM plano de decoder de hardware; com o live
  // segurando, o `<video>` do episódio recebe o arquivo (2xx) e falha com
  // `error.code` 3/4 — e o player classifica como "não toca neste aparelho",
  // culpando o arquivo por um defeito do app.
  //
  // O veredito vem da PILHA ("há QUALQUER camada por cima?", `overlayCoversLive`),
  // lido síncrono por um listener do store — NÃO de um efeito de render. Qualquer
  // camada, e não só player: ficha e player são opacos por cima do consumidor, e
  // com "só player" o smoke da LG (2.39.0) viu o canal voltar a tocar ATRÁS da
  // ficha na saída do episódio. O live só toca com a superfície livre: pilha
  // VAZIA e rota do live. O store renderiza em
  // SyncLane (`useSyncExternalStore`), antes de um `setState` comum; amarrar isto
  // a render/efeito seria apostar na ordem de lane (a cascata de 52 episódios do
  // auto-avanço nasceu de uma aposta assim). O listener roda DENTRO do
  // `pushPlayer`/`pop`, antes de o React montar o player — o live solta o
  // decodificador antes de o `<video>` do episódio receber o `src`.
  //
  // Suspender aqui é DESTRUIR o player (depois de silenciar na hora), não o
  // `doSuspend` da rota, por três razões medidas neste arquivo e no WebPlayer:
  //  1. Só o destroy solta o decodificador em TODOS os ramos. O `suspend()` do
  //     player remove o `src` no native-ts (webOS), mas no mpegts/Shaka (web,
  //     Tizen, Android) só descarrega o transmuxer: o `src` blob do MSE fica
  //     preso ao `<video>` até o GC (10s) — com um episódio começando nesse meio.
  //  2. Uma carga já ENTREGUE ao player (o `runLoad` na fila) roda DEPOIS de um
  //     `suspend()`: o `.ts` pousa no `src` (o webOS já começa a pegar o plano)
  //     antes de o `runSuspend` tirá-lo. O destroy marca a instância como
  //     destruída na hora e o `runLoad` recusa — o `.ts` nunca pousa. Gate:
  //     `overlay-suspend.dom.test.tsx`, "carga já ENTREGUE" (o play() tardio dessa
  //     janela tem ainda uma 2ª defesa: o gate de mídia armada no 'ready').
  //  3. Não custa o gesto de autoplay que o suspend leve da ROTA protege: o
  //     `<video>` continua o MESMO elemento persistente no portal (só a instância
  //     do player é refeita), e um player de overlay dura minutos — o GC da rota
  //     já teria destruído o live do mesmo jeito. Voltar recarrega pelo `play()`,
  //     como o consumidor faria ao remontar.
  const suspendForOverlay = useCallback(() => {
    // O que volta na saída: o último pedido aceito, SE o live estava vivo (player
    // de pé ou carga armada). Um live já coletado pelo GC não tem o que voltar —
    // quem remontar pede de novo.
    if (playerRef.current !== null || mediaArmedRef.current) {
      overlayResumeRef.current = liveRequestRef.current
    }
    // Carga EM VOO não pousa depois: invalida o token, e o `doLoad` que esperava
    // o sign aborta no próximo checkpoint (antes de `player.load()`).
    loadGateRef.current!.begin()
    // Silêncio NA HORA: o teardown do destroy é assíncrono (espera Shaka/mpegts
    // soltarem o MSE antes de mexer no `<video>`).
    const v = videoRef.current
    if (v) {
      try {
        v.pause()
      } catch {
        // ignora
      }
      v.muted = true
    }
    // Solta o decodificador: pause + remove `src` + `load()` no `runDestroy`.
    // Também zera timers/back-off/gesto e reconcilia o stage (esconde).
    void doDestroy()
  }, [doDestroy])

  const resumeFromOverlay = useCallback(() => {
    const target = overlayResumeRef.current
    overlayResumeRef.current = null
    if (target === null) return
    // "Retoma quando a pilha fica VAZIA e a rota é a do live" (o listener só
    // chama isto na transição pra pilha vazia): fora da família live o canal
    // carregaria invisível, ocupando o decodificador, e quem traz o live de volta
    // é o consumidor ao montar.
    if (!inLiveRoute()) return
    // E só com consumidor NO DOCUMENTO: sem ele ninguém mostra o vídeo (no ramo
    // fixo o stage fica escondido; no appendChild o `<video>` está na layer
    // escondida) e o áudio tocaria sozinho.
    const c = currentContainerRef.current
    if (c === null || !c.isConnected) return
    // Pela MESMA porta pública do consumidor: dedup, fonte manual (IDB) e o sign
    // otimista — voltar do player funciona como remontar o Hero.
    play(target.channelId, target.group)
  }, [play])

  // Ref estável pro listener do store (que não re-assina a cada render). Mesmo
  // padrão do `actionsImplRef`.
  const overlayGuardRef = useRef({ suspendForOverlay, resumeFromOverlay })
  useEffect(() => {
    overlayGuardRef.current = { suspendForOverlay, resumeFromOverlay }
  }, [suspendForOverlay, resumeFromOverlay])

  useEffect(() => {
    const apply = (): void => {
      try {
        const blocked = overlayCoversLive()
        // Age na TRANSIÇÃO. O veredito é função só da pilha, e este listener vê
        // TODA mutação dela (roda dentro do `emit`); o check inicial abaixo cobre
        // o que mudou antes de a assinatura existir (deep-link frio no boot).
        if (blocked === overlayBlockedRef.current) return
        overlayBlockedRef.current = blocked
        if (blocked) overlayGuardRef.current.suspendForOverlay()
        else overlayGuardRef.current.resumeFromOverlay()
      } catch (err) {
        // Nunca lança: o `emit` do store não isola listeners, e um throw aqui
        // deixaria o do React sem aviso da mutação.
        console.warn('[live-player] guard de overlay falhou (non-fatal):', err)
      }
    }
    apply()
    return subscribeOverlayStack(apply)
  }, [])

  // ===== audio-only: decidido pelo demuxer, NÃO por `videoWidth` =====
  // O latch de `loadedmetadata` (v.videoWidth===0 → audio-only) foi REMOVIDO:
  // era o falso-positivo principal. No MSE (mpegts.js) o `loadedmetadata`
  // dispara assim que o PRIMEIRO init segment é anexado — em TS ao vivo o áudio
  // costuma vir antes do vídeo, então `videoWidth===0` nesse tick NÃO prova
  // ausência de vídeo, e o latch escondia o vídeo real atrás do overlay de
  // rádio. A verdade agora vem do `MEDIA_INFO` do demuxer (handler 'mediainfo'
  // acima), que reporta `hasVideo` do PMT antes do 1º frame. Ver
  // docs/reports do fix tv-audio-only-false-positive.

  // Online/offline: detecta perda de conexão. Quando volta online,
  // recarrega o canal atual (network drop pode ter quebrado o stream).
  useEffect(() => {
    // Timestamp do último offline. Usado pra decidir se attempted deve
    // resetar ao voltar online. Drop transitório (< 60s) NÃO reseta —
    // falhas recentes ainda são relevantes (a fonte que acabou de
    // morrer provavelmente continua morta). Standby longo (>60s) reseta
    // — todas as sources do set podem ter caducado em paralelo.
    // (m7 review auto-switch 2026-05-11.)
    let offlineAt: number | null = null
    // Threshold mais conservador que o de visibility (30s — ver useEffect
    // logo abaixo): rede oscila com mais ruído (drops de 5-30s comuns em
    // Wi-Fi/4G), enquanto visibility=visible é intenção ativa do user.
    const OFFLINE_RESET_THRESHOLD_MS = 60_000
    const onOnline = () => {
      setState((s) => ({ ...s, online: true }))
      // Volta online — força reload do canal atual pra reabrir conexão.
      //
      // P2 #6 do review auto-switch (2026-05-11): RESETA attemptedSourcesRef
      // antes do reload SE offline > 60s. Em standby longo (1h+), todas
      // as sources do attempted set provavelmente caducaram simultaneamente.
      // Sem reset, o reload tentava `sources[0]` (que tava no set) →
      // wrap-around procurava !attempted → vazio → "esgotou" sem tentar
      // nenhuma. Drop transitório (<60s) preserva attempted — falhas
      // recentes ainda valem. Manual NÃO é resetado em nenhum caso
      // (escolha do user persiste pós-standby).
      const offlineDurationMs =
        offlineAt !== null ? Date.now() - offlineAt : Infinity
      if (offlineDurationMs > OFFLINE_RESET_THRESHOLD_MS) {
        attemptedSourcesRef.current.clear()
        // F3 — standby longo (>60s offline): os endpoints que tinham caído
        // provavelmente se recuperaram; zera o anti-loop de endereços também.
        attemptedEndpointsRef.current.clear()
        endpointFailoverCountRef.current = 0
        track({ type: 'attempted_reset', source: 'online', ts: Math.floor(Date.now() / 1000) })
      }
      offlineAt = null
      const cur = stateRef.current
      if (cur.channelId && cur.group) {
        doLoadRef.current?.(cur.channelId, cur.group, /* keepAttempted */ true)
      }
    }
    const onOffline = () => {
      offlineAt = Date.now()
      setState((s) => ({ ...s, online: false }))
    }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  // Bug 4 (decisão Fernando 2026-05-14): reset de attemptedSourcesRef ao
  // voltar do background (visibilitychange → 'visible'), com threshold de
  // tempo. Em standby longo (TV em sleep, tab oculta no celular do user
  // testando), as fontes que falharam podem ter se recuperado — dar fresh
  // start em vez de mostrar "Tentar de novo" cedo demais.
  //
  // Threshold de 30s evita thrashing quando usuário alterna janelas
  // rapidamente (cenário irrelevante em TV mas importante em web/dev).
  // Análogo ao reset do `onOnline` (>60s) — diferente da rede, voltar
  // visível em 30s já basta pra valer o reset (foreground == intenção
  // ativa do user, diferente de "rede flutuou").
  //
  // CRÍTICO: NÃO faz reload do canal aqui. Diferente do `onOnline` (que
  // sabe que a conexão TCP precisa renegociar), `visibilitychange` por si
  // só não invalida o stream — o player pode até estar tocando ainda
  // (background audio). Só preparamos o terreno: na próxima falha real,
  // o auto-switch tem o set limpo pra rotacionar de novo.
  useEffect(() => {
    let hiddenAt: number | null = null
    const HIDDEN_RESET_THRESHOLD_MS = 30_000
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      // visible
      const hiddenDurationMs =
        hiddenAt !== null ? Date.now() - hiddenAt : Infinity
      hiddenAt = null
      if (hiddenDurationMs > HIDDEN_RESET_THRESHOLD_MS) {
        attemptedSourcesRef.current.clear()
        // F3 — voltou do background após >30s: prepara terreno pra failover de
        // endereço também (igual ao set de fontes — só na próxima falha real).
        attemptedEndpointsRef.current.clear()
        endpointFailoverCountRef.current = 0
        track({ type: 'attempted_reset', source: 'visibility', ts: Math.floor(Date.now() / 1000) })
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  // Flush do attach enfileirado. LAYOUT effect de propósito: roda DEPOIS dos
  // layout effects dos filhos e DEPOIS do ref do portal, ainda dentro do MESMO
  // commit — que é a condição inteira do handoff (o <video> não pode atravessar
  // fronteira de task fora do documento). Sem array de deps porque a condição é
  // "algum filho pediu attach neste commit?", não uma dependência de valor; o
  // corpo sai fora em 2 leituras de ref quando não há nada pendente.
  useLayoutEffect(() => {
    const pending = pendingAttachRef.current
    if (!pending || !videoRef.current) return
    pendingAttachRef.current = null
    attachTo(pending.container, pending.mode)
  })

  // Cleanup global no unmount do provider (ex: logout, app close)
  useEffect(() => {
    return () => {
      // Unmount do Provider (logout, close app). Cleanup do useEffect
      // não pode ser async — fire-and-forget é OK (app está saindo;
      // ninguém vai reusar o player).
      cancelPark()
      void doDestroy()
    }
  }, [cancelPark, doDestroy])

  // Route guard: destrói singleton imediato quando user navega pra
  // fora das rotas que CONSOMEM live (LiveScreen / PlayerScreen
  // fullscreen). Sem isso, o GC de 30s mantém o video tocando em
  // hidden layer com áudio audível em outras telas — bug reportado:
  // user vai pra EPG e ouve áudio do canal anterior.
  //
  // PORQUÊ no Provider e não em screen-level cleanup:
  // - LiveHeroPreview unmount já chama attachTo(null) — mas isso só
  //   move pra hidden + agenda GC, não para o áudio.
  // - PlayerScreen unmount idem (precisa manter vivo se user volta
  //   pra LiveScreen).
  // - A única autoridade que sabe "user saiu da família live" é o
  //   router. Centralizar aqui evita duplicar a regra em N screens.
  //
  // POR QUE NÃO useLocation: este Provider vive FORA do RouterProvider
  // (precisa do QueryClient acima, e routes.tsx vive dentro do router).
  // useLocation quebra com "may be used only in the context of a Router".
  // Solução: escutar popstate (BACK/forward do browser) + monkey-patch
  // de pushState/replaceState (programmatic navigation do react-router).
  // É a forma canônica de spy em URL fora de qualquer Router.
  useEffect(() => {
    const checkRoute = () => {
      // basename-aware: no empacotado o pathname vem com o caminho de instalação
      // inteiro. Testar o pathname cru fazia inLive ser SEMPRE false na TV →
      // doSuspend em toda navegação → live muda e pausada. Ver live-route.ts.
      if (!inLiveRoute()) {
        doSuspend()
        return
      }
      // NÃO reconcilie aqui. Este ramo já teve um `reconcileStage()`, posto
      // como "segundo caminho de mostrar" — e ele era INERTE: depois que o
      // invariante passou a exigir mídia armada, sair da família live passa
      // SEMPRE pelo `doSuspend`, que desarma a mídia, então não existe estado
      // "deveria mostrar, mas está escondido" pra este caminho recuperar. Quem
      // devolve o stage é o `doLoad` (a mídia rearmando) e o `attachTo` (o
      // consumidor montando). Nenhum mutante conseguia matar a chamada daqui —
      // o sinal de que ela não valia o que prometia.
      // REENTRADA na família de live: o usuário está voltando. Dá fôlego ao GC.
      //
      // Quem cancela o GC de verdade é o `attachTo(container)` — mas ele só roda
      // quando o Hero MONTA, e na LG real isso levou ~7s (a LiveScreen espera a
      // lista de canais). Com a janela de suspend em 10s, o GC destruía o player
      // JUSTO enquanto o usuário voltava. Aqui esticamos pra janela HIDDEN (30s),
      // que é a semântica certa: "estou na live, alguém já vai attachar". Se
      // ninguém attachar mesmo assim, o GC dispara e destrói normalmente.
      if (playerRef.current && currentContainerRef.current === null && gcTimerRef.current !== null) {
        firstSuspendAtRef.current = null // cadeia de suspend nova quando sair de novo
        scheduleGc(HIDDEN_GC_DELAY_MS)
      }
    }

    // Patch pushState/replaceState pra disparar event customizado.
    // Sem isso, navegação programática do react-router-dom não
    // dispara popstate (só BACK/forward do browser fazem).
    //
    // HMR guard: marca a função patched com flag. Se já está patched
    // (Fast Refresh remontou Provider), reusa wrapper existente em vez
    // de empilhar camadas (cada HMR criava +1 wrapper → fire() N vezes).
    type PatchedFn = typeof window.history.pushState & { __livePatched?: true }
    const pushFn = window.history.pushState as PatchedFn
    const replaceFn = window.history.replaceState as PatchedFn
    const alreadyPatched = pushFn.__livePatched === true && replaceFn.__livePatched === true

    const origPush = pushFn
    const origReplace = replaceFn
    const fire = () => window.dispatchEvent(new Event('locationchange'))

    if (!alreadyPatched) {
      const wrappedPush = function (this: History, ...args: Parameters<History['pushState']>) {
        origPush.apply(this, args)
        fire()
      } as PatchedFn
      wrappedPush.__livePatched = true
      const wrappedReplace = function (this: History, ...args: Parameters<History['replaceState']>) {
        origReplace.apply(this, args)
        fire()
      } as PatchedFn
      wrappedReplace.__livePatched = true
      window.history.pushState = wrappedPush
      window.history.replaceState = wrappedReplace
    }

    window.addEventListener('popstate', checkRoute)
    window.addEventListener('locationchange', checkRoute)
    // Check inicial: se Provider monta numa rota que não é live, já
    // limpa (raro — Provider tipicamente monta uma vez no boot).
    checkRoute()
    return () => {
      // Restauração DEFENSIVA: só desfaz patch se a função atual ainda
      // é a NOSSA (não foi sobrescrita por outra lib/extensão entre
      // mount e unmount). Sem essa guarda, descartaríamos patch alheio
      // e quebraríamos a chain.
      // Em prática Provider só desmonta em logout/HMR — vale o cuidado.
      if (!alreadyPatched) {
        const cur = window.history.pushState as PatchedFn
        const curReplace = window.history.replaceState as PatchedFn
        if (cur.__livePatched === true) window.history.pushState = origPush
        if (curReplace.__livePatched === true) window.history.replaceState = origReplace
      }
      window.removeEventListener('popstate', checkRoute)
      window.removeEventListener('locationchange', checkRoute)
    }
  }, [doSuspend, scheduleGc])

  // Actions context: estável via ref. As funções internas (play, attachTo
  // etc) mudam quando deps mudam, mas a interface exposta é wrapper que
  // delega via ref atualizado. Resultado: identity dos callbacks NUNCA
  // muda → consumers não re-renderizam quando state muda.
  const actionsImplRef = useRef({
    play,
    selectSource,
    retry,
    attachTo,
    subscribe,
    destroy,
  })
  useEffect(() => {
    actionsImplRef.current = { play, selectSource, retry, attachTo, subscribe, destroy }
  }, [play, selectSource, retry, attachTo, subscribe, destroy])

  const stableActions = useMemo<LivePlayerActions>(
    () => ({
      play: (channelId, group) => actionsImplRef.current.play(channelId, group),
      selectSource: (channelId) => actionsImplRef.current.selectSource(channelId),
      retry: () => actionsImplRef.current.retry(),
      attachTo: (container, mode) => actionsImplRef.current.attachTo(container, mode),
      subscribe: (event, handler) =>
        actionsImplRef.current.subscribe(event, handler),
      destroy: () => actionsImplRef.current.destroy(),
    }),
    [],
  )

  return (
    <LivePlayerActionsContext.Provider value={stableActions}>
      <LivePlayerStateContext.Provider value={state}>
        {children}
        {/* Portal pra layer escondida — singleton home. <video> vive aqui
            quando nenhum componente attached. document.body como target
            evita re-render quando children mudam.

            ⚠️ COLISÃO IMPERATIVO × DECLARATIVO (native-ts / fixedVideoMode):
            no ramo fixo este <div> é o STAGE, e applyStageStyle escreve
            position/top/left/width/height/opacity/z-index/overflow via
            `style.setProperty` IMPERATIVO (fora do controle do React). O `style`
            declarativo abaixo mexe NAS MESMAS props. Isso só convive porque o
            objeto `style` é um literal com VALORES CONSTANTES: o React faz diff
            do style prop contra o render anterior (idêntico) e NÃO retoca o DOM,
            preservando o que o setProperty aplicou. Se alguém tornar qualquer
            valor DEPENDENTE DE STATE/PROP, o React vai reaplicar essa prop a cada
            render e CLOBBERAR a posição do stage (vídeo salta pro 1x1 escondido).
            Regra: este objeto style permanece 100% constante — reposicionamento
            é SEMPRE imperativo (applyStageStyle), nunca declarativo. */}
        {createPortal(
          <div
            ref={hiddenLayerRef}
            aria-hidden="true"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
              zIndex: -1,
            }}
          >
            <video
              ref={videoRef}
              // áudio sempre ligado (decisão de produto). muted=false
              // explícito pra evitar default browser autoplay-without-sound.
              muted={false}
              playsInline
              // class="livePlayerVideo" pra container decidir o styling
              // (Hero usa object-fit: cover; Fullscreen usa contain).
              className="livePlayerVideo"
              style={{ width: '100%', height: '100%', background: '#000' }}
            />
          </div>,
          document.body,
        )}
      </LivePlayerStateContext.Provider>
    </LivePlayerActionsContext.Provider>
  )
}

// ===== Hooks =====

/**
 * Actions estáveis (NUNCA mudam de identity). Consumers que só precisam
 * chamar funções (não ler state) devem usar este — re-renders zero.
 */
export function useLivePlayerActions(): LivePlayerActions {
  const ctx = useContext(LivePlayerActionsContext)
  if (!ctx) {
    throw new Error('useLivePlayerActions must be used within <LivePlayerProvider>')
  }
  return ctx
}

/**
 * Estado reativo do player. Re-render quando state muda. Use só onde
 * você realmente precisa ler status/channelId/etc na UI.
 */
export function useLivePlayerState(): LivePlayerState {
  const ctx = useContext(LivePlayerStateContext)
  if (!ctx) {
    throw new Error('useLivePlayerState must be used within <LivePlayerProvider>')
  }
  return ctx
}

/**
 * Combina state + actions num objeto (compat com chamadas antigas).
 * IMPORTANTE: o objeto retornado SEMPRE tem identity nova (porque state
 * muda) — NÃO use em deps de useEffect. Prefira useLivePlayerActions
 * + useLivePlayerState separados.
 */
export function useLivePlayer(): {
  state: LivePlayerState
} & LivePlayerActions {
  const state = useLivePlayerState()
  const actions = useLivePlayerActions()
  return { state, ...actions }
}

export type { LivePlayerStatus } from './types'
